<template>
    <!-- Chore preview / editable form -->
    <div v-if="msg.type === 'chore_form'" class="ce-card ce-card--form">
        <div class="ce-card__header">
            <div>
                <div class="ce-card__title"> Details</div>
                <div class="ce-card__sub">
                    {{ msg.payload?.response || ('Please review and update the ' + lblChoreLower + ' before saving.') }}
                </div>
            </div>
        </div>

        <div class="ce-card__body">
            <!-- Chore Title - Mandatory -->
            <div class="ce-chore-form-field">
                <label class="ce-chore-form-label">
                    {{ lblChoreTitle }} <span class="ce-required">*</span>
                </label>

                <input v-model="choreForm.choreTitle"
                       type="text"
                       class="ce-input"
                       maxlength="200"
                       :disabled="disabled"
                       placeholder="E.G. Kitchen Cleaning" />

                <div v-if="choreValidationTouched && !String(choreForm.choreTitle || '').trim()"
                     class="ce-chore-validation-error">
                    {{ lblChoreTitle }} is required.
                </div>
            </div>

            <!-- Frequency - Mandatory -->
            <div class="ce-chore-form-field">
                <label class="ce-chore-form-label">
                    {{ lblFrequency }} <span class="ce-required">*</span>
                </label>

                <!-- Native select: the list opens in the OS layer, so it can
                     never end up behind the chatbot window on mobile. -->
                <select v-model="choreForm.frequency"
                        class="ce-input ce-select"
                        :disabled="disabled"
                        @change="onChoreFrequencyChange">
                    <option value="" disabled>Select Frequency</option>
                    <option v-for="opt in choreFrequencyFormOptions"
                            :key="opt.value"
                            :value="opt.value">
                        {{ opt.text }}
                    </option>
                </select>

                <div v-if="choreValidationTouched && !choreForm.frequency"
                     class="ce-chore-validation-error">
                    {{ lblFrequency }} is required.
                </div>
            </div>

            <!-- Week Days - editable only for Weekly frequency -->
            <div v-if="isWeeklyChoreFrequency" class="ce-chore-form-field">
                <label class="ce-chore-form-label">
                    Week Days <span class="ce-required">*</span>
                </label>

                <details class="ce-assignees ce-weekdays-dropdown">
                    <summary>{{ choreFormWeekDaysLabel }}</summary>

                    <div class="ce-assignees-panel">
                        <label class="ce-assignees-all">
                            <input type="checkbox"
                                   :checked="allChoreFormWeekDaysSelected"
                                   :disabled="disabled"
                                   @change="toggleAllChoreFormWeekDays($event.target.checked)" />
                            <span>Select All</span>
                        </label>

                        <label v-for="day in choreWeekdayOptions" :key="day.value">
                            <input v-model="choreForm.weekDays"
                                   type="checkbox"
                                   :value="day.value"
                                   :disabled="disabled" />
                            <span>{{ day.text }}</span>
                        </label>
                    </div>
                </details>

                <div v-if="choreValidationTouched && !choreForm.weekDays.length"
                     class="ce-chore-validation-error">
                    Week Days is required for Weekly frequency.
                </div>
            </div>

            <!-- Assign Roommate - Mandatory -->
            <div class="ce-chore-form-field">
                <label class="ce-chore-form-label">
                    {{ lblAssignPerson }} <span class="ce-required">*</span>
                </label>

                <details class="ce-assignees">
                    <summary>{{ choreForm.roommateNames.join(', ') || 'Select Roommates' }}</summary>
                    <div class="ce-assignees-panel">
                        <label class="ce-assignees-all">
                            <input type="checkbox"
                                   :checked="choreFormRoommateOptions.length > 0 && choreFormRoommateOptions.every(item => choreForm.roommateIds.includes(item.value))"
                                   :disabled="disabled || !choreFormRoommateOptions.length"
                                   @change="toggleAllChoreFormRoommates($event.target.checked)" />
                            <span>Select All</span>
                        </label>
                        <label v-for="item in choreFormRoommateOptions" :key="item.value">
                            <input v-model="choreForm.roommateIds" type="checkbox"
                                   :value="item.value" :disabled="disabled"
                                   @change="onChoreFormRoommateChange" />
                            <span>{{ item.text }}</span>
                        </label>
                    </div>
                </details>

                <div v-if="choreValidationTouched && !choreForm.roommateIds.length"
                     class="ce-chore-validation-error">
                    {{ lblAssignPerson }} is required.
                </div>
            </div>

            <!-- Start Date - Optional in portal (PrimeVue Calendar) -->
            <div class="ce-chore-form-field">
                <label class="ce-chore-form-label">Start Date</label>

                <Calendar v-model="choreFormDueDateModel"
                          dateFormat="mm-dd-yy"
                          placeholder="mm-dd-yyyy"
                          :minDate="choreMinDate"
                          :manualInput="false"
                          :disabled="disabled"
                          class="w-100 ce-calendar"
                          inputClass="ce-input w-100"
                          :showIcon="false" />
            </div>

            <!-- Notes - Optional -->
            <div class="ce-chore-form-field ce-chore-form-field--last">
                <label class="ce-chore-form-label">Notes</label>

                <textarea v-model="choreForm.notes"
                          class="ce-input"
                          rows="3"
                          maxlength="1000"
                          :disabled="disabled"
                          placeholder="Optional Details..."></textarea>
            </div>
        </div>

        <div class="ce-card__actions">
            <button type="button"
                    class="ce-btn ce-btn--primary"
                    :disabled="disabled"
                    @click="onConfirmChore">
                Save {{ lblChore }}
            </button>

            <button type="button"
                    class="ce-btn ce-btn--link"
                    :disabled="disabled"
                    @click="$emit('cancel')">
                Cancel
            </button>
        </div>
    </div>

    <!-- Chore created / failed -->
    <div v-else-if="msg.type === 'chore_created' || msg.type === 'chore_failed'" class="ticket-preview-card">
        <div class="ticket-preview-header" :style="isChoreError ? HEADER_ERROR : null">

            <div>
                <h4>{{ isChoreError ? (lblChore + ' Not Saved') : (lblChore + ' Created!') }}</h4>
                <p>{{ isChoreError ? 'Something went wrong.' : ('The ' + lblChoreLower + ' has been created.') }}</p>
            </div>
        </div>
        <div class="ticket-preview-body">
            <div class="ticket-preview-row"><span class="tp-label">Title</span><span class="tp-value">{{ choreData.title || '—' }}</span></div>
            <div class="ticket-preview-row"><span class="tp-label">Asset</span><span class="tp-value">{{ choreData.assetName || '—' }}</span></div>
            <div class="ticket-preview-row"><span class="tp-label">Frequency</span><span class="tp-value">{{ choreData.frequency || '—' }}</span></div>
            <div class="ticket-preview-row"><span class="tp-label">Due</span><span class="tp-value">{{ formatMMDDYYYY(choreData.dueDate) }}</span></div>
            <div class="ticket-preview-row"><span class="tp-label">Assigned To</span><span class="tp-value">{{ joinNames(choreData.roommateNames) }}</span></div>
            <div v-if="isChoreError" class="ticket-preview-error">

                <p><strong>Error:</strong> {{ choreData.error || 'Please try again.' }}</p>
            </div>
        </div>
        <div class="ticket-preview-footer">
            <span> {{ formatMMDDYYYY(currentDate) }}</span>
            <span :style="{ color: isChoreError ? '#dc2626' : '#16a34a' }">{{ isChoreError ? 'Not Saved' : 'Saved' }}</span>
        </div>
    </div>

    <!-- Chore list -->
    <div v-else-if="msg.type === 'view_chores'" class="ticket-preview-card">
        <div class="ticket-preview-header">

            <div><h4>{{ choreData.heading || ('Your ' + lblChorePlural) }}</h4></div>
            <span class="ticket-preview-number">{{ chores.length }} {{ lblChoreLower }}{{ chores.length !== 1 ? 's' : '' }}</span>
        </div>
        <div class="ticket-preview-body ce-list-body">
            <div v-if="!chores.length" class="ce-empty">No {{ lblChoreLower }}s found.</div>
            <div v-for="(c, idx) in chores" :key="c.id || idx" class="ce-list-item" :class="{ 'ce-list-item--last': idx === chores.length - 1 }">
                <div class="ce-list-item__main">
                    <div class="ce-list-item__title">{{ c.title }}</div>
                    <div class="ce-list-item__tags">
                        <button type="button"
                                class="ce-pill ce-chore-status-clickable"
                                :style="pillStyle(choreStatusColor(c.status))"
                                :disabled="disabled"
                                :title="'Change ' + lblChoreLower + ' status'"
                                @click="requestQuickChoreStatusChange(c)">
                            {{ c.status }}
                        </button>
                        <span class="ce-list-item__due"> {{ formatMMDDYYYY(c.dueDate) }} · {{ c.frequency }}</span>
                    </div>


                </div>

                <div class="ce-list-item__actions">
                    <button type="button"
                            class="ce-btn ce-btn--accent-link"
                            :disabled="disabled"
                            @click="toggleChoreDetails(c, idx)">
                        {{ isChoreDetailsOpen(c, idx) ? 'Hide ▲' : 'Details ▼' }}
                    </button>
                    <button type="button"
                            class="ce-btn ce-btn--danger-link"
                            :disabled="disabled"
                            @click="requestChoreDelete(c)">
                        Remove
                    </button>
                </div>

                <div v-if="isChoreDetailsOpen(c, idx)" class="ce-chore-details">
                    <div class="ce-chore-detail-row"><span>Assigned To</span><strong>{{ c.assignedUserName || '—' }}</strong></div>
                    <div class="ce-chore-detail-row"><span>Status</span><strong>{{ c.status || '—' }}</strong></div>
                    <div class="ce-chore-detail-row"><span>Due Date</span><strong>{{ formatMMDDYYYY(c.dueDate) }}</strong></div>
                    <div class="ce-chore-detail-row"><span>Frequency</span><strong>{{ c.frequency || '—' }}</strong></div>
                    <div v-if="hasChoreWeekDays(c)" class="ce-chore-detail-row">
                        <span>Week Days</span>
                        <strong>{{ formatChoreWeekDays(c) }}</strong>
                    </div>
                    <!--<div class="ce-chore-detail-row"><span>Priority</span><strong>{{ c.priority || '—' }}</strong></div>-->
                    <div class="ce-chore-detail-row"><span>Rotation Type</span><strong>{{ c.rotationType || '—' }}</strong></div>
                </div>

            </div>
        </div>
    </div>

    <!-- Chore status update picker -->
    <div v-else-if="msg.type === 'chore_status_update_flow'"
         class="ce-status-flow-card">

        <div class="ce-status-flow-header">
            {{ choreStatusUpdateQuestion }}
        </div>

        <div class="ce-status-flow-options">
            <button v-for="(option, index) in choreStatusUpdateOptions"
                    :key="index"
                    type="button"
                    class="ce-status-flow-option"
                    :disabled="disabled"
                    @click="$emit('select-chore-status-update', option.value, option.label)">
                <span>{{ option.label }}</span>
                <span class="ce-status-flow-arrow">→</span>
            </button>

            <div v-if="!choreStatusUpdateOptions.length"
                 class="ce-empty">
                No options available.
            </div>
        </div>
    </div>

    <!-- Chore / Task frequency picker -->
    <div v-else-if="msg.type === 'chore_frequency_options'" class="ce-card ce-frequency-card">
        <div class="ce-card__header">
            <div>
                <div class="ce-card__title">{{ lblFrequency }}</div>
                <div class="ce-card__sub">
                    {{ msg.payload?.response || ('How often should this ' + lblChoreLower + ' repeat?') }}
                </div>
            </div>
        </div>
        <div class="ce-card__body ce-frequency-options" style="display:flex;flex-direction:column;gap:8px;">
            <button v-for="option in (msg.payload?.chore_data?.options || [])"
                    :key="option.value || option.id || option"
                    type="button"
                    class="ce-frequency-option-btn"
                    :disabled="disabled"
                    :style="{
                        width: '100%',
                        minHeight: '42px',
                        padding: '10px 14px',
                        border: '0',
                        borderRadius: '10px',
                        background: 'linear-gradient(135deg, rgb(15, 102, 184), rgb(18, 35, 65))',
                        color: '#ffffff',
                        fontFamily: 'inherit',
                        fontSize: '13px',
                        fontWeight: '700',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        opacity: disabled ? '0.5' : '1',
                        boxSizing: 'border-box',
                        margin: '0'
                    }"
                    @click="$emit('select-chore-frequency', option.value || option.text || option)">
                {{ option.text || option.name || option }}
            </button>
        </div>
    </div>

    <!-- Weekly chore day picker - no header icon -->
    <div v-else-if="msg.type === 'chore_weekday_options'"
         class="ce-weekdays-card">
        <div class="ce-weekdays-header">
            {{ choreWeekdaysPromptText }}
        </div>

        <div class="ce-weekdays-options">
            <button v-for="day in choreWeekdayOptions"
                    :key="day.value"
                    type="button"
                    class="ce-weekday-btn"
                    :class="{ 'ce-weekday-btn--selected': selectedChoreWeekDays.includes(day.value) }"
                    :disabled="disabled"
                    @click="toggleChoreWeekDay(day.value)">
                {{ day.label }}
            </button>
        </div>

        <button type="button"
                class="ce-weekdays-confirm"
                :disabled="disabled || !selectedChoreWeekDays.length"
                @click="confirmChoreWeekDays">
            Confirm
        </button>
    </div>

    <!-- Chore roommate picker -->
    <div v-else-if="msg.type === 'chore_roommate_options'" class="ce-card">
        <div class="ce-card__header">
            <span class="ce-card__icon"></span>
            <div>
                <div class="ce-card__title">{{ lblAssignPersonPlural }}</div>
                <div class="ce-card__sub">{{ msg.payload?.response || ('Select ' + (isBoatAsset ? 'co-owners' : 'roommates') + ' for this ' + lblChoreLower) }}</div>
            </div>
        </div>
        <div class="ce-card__body">

            <!-- Select All -->
            <label class="ce-roommate-select-all">
                <input type="checkbox"
                       :checked="allChoreRoommatesSelected"
                       :disabled="disabled || !choreRoommateOptions.length"
                       @change="toggleAllChoreRoommates" />
                <span>Select All</span>
            </label>

            <div class="ce-roommate-divider"></div>

            <!-- Existing roommate list -->
            <label v-for="roommate in choreRoommateOptions"
                   :key="roommate.id || roommate.value"
                   class="ce-roommate-option">
                <input v-model="selectedChoreRoommateIds"
                       type="checkbox"
                       :value="String(roommate.id || roommate.value)"
                       :disabled="disabled" />
                <span>{{ getChoreRoommateDisplayName(roommate) }}</span>
            </label>
        </div>
        <div class="ce-card__actions">
            <button type="button"
                    class="ce-btn ce-btn--primary"
                    :disabled="disabled || !selectedChoreRoommateIds.length"
                    @click="$emit('select-chore-roommates', selectedChoreRoommateIds)">
                Confirm Selection
            </button>
            <button type="button" class="ce-btn ce-btn--link" :disabled="disabled" @click="$emit('cancel')">Cancel</button>
        </div>
    </div>

    <!-- Event Guest Source: From Roommates / Another Guest -->
    <div v-else-if="(msg.type === 'event_guest_source_prompt' || msg.type === 'event_flow') && isEventGuestSourcePrompt"
         class="ce-event-guest-card ce-event-guest-source-card">

        <div class="ce-event-guest-header ce-event-guest-source-header">
            {{ eventGuestPromptText }}
        </div>

        <div class="ce-event-guest-source-options">
            <button type="button"
                    class="ce-event-guest-source-option"
                    :disabled="disabled"
                    @click="$emit('select-event-guest-source', 'roommate')">
                <span class="ce-event-guest-source-option__text">
                    {{ isBoatAsset ? 'From Co-owners' : 'From Roommates' }}
                </span>
                <span class="ce-event-guest-source-option__arrow">→</span>
            </button>

            <button type="button"
                    class="ce-event-guest-source-option"
                    :disabled="disabled"
                    @click="$emit('select-event-guest-source', 'other')">
                <span class="ce-event-guest-source-option__text">
                    Another Guest
                </span>
                <span class="ce-event-guest-source-option__arrow">→</span>
            </button>
        </div>
    </div>

    <!-- Event Guest Roommate Picker - multi-select with Select All (same pattern as chore roommate picker) -->
    <div v-else-if="(msg.type === 'event_guest_roommate_prompt' || msg.type === 'event_flow') && isEventGuestRoommatePrompt"
         class="ce-event-guest-card">

        <div class="ce-event-guest-header">
            {{ eventGuestPromptText }}
        </div>

        <div class="ce-card__body" style="padding:12px 14px;">
            <!-- Select All -->
            <label class="ce-roommate-select-all">
                <input type="checkbox"
                       :checked="allEventGuestRoommatesSelected"
                       :disabled="disabled || !eventGuestRoommateDropdownOptions.length"
                       @change="toggleAllEventGuestRoommates($event.target.checked)" />
                <span>Select All</span>
            </label>

            <div class="ce-roommate-divider"></div>

            <label v-for="roommate in eventGuestRoommateDropdownOptions"
                   :key="roommate.value"
                   class="ce-roommate-option">
                <input v-model="selectedEventGuestRoommateIds"
                       type="checkbox"
                       :value="String(roommate.value)"
                       :disabled="disabled" />
                <span>{{ roommate.text }}</span>
            </label>

            <div v-if="!eventGuestRoommateDropdownOptions.length"
                 class="ce-event-guest-empty">
                No {{ isBoatAsset ? 'members' : 'roommates' }} found.
            </div>
        </div>

        <div class="ce-card__actions" style="padding:0 14px 14px;">
            <button type="button"
                    class="ce-btn ce-btn--primary"
                    :disabled="disabled || !selectedEventGuestRoommateIds.length"
                    @click="confirmEventGuestRoommateSelection">
                Confirm Selection
            </button>
        </div>
    </div>

    <!-- Event Guest Phone Number -->
    <div v-else-if="msg.type === 'event_flow' && isEventGuestPhonePrompt"
         class="ce-phone-card">

        <div class="ce-phone-question"
             v-html="formatEventPhonePrompt(responseText)">
        </div>

        <div class="ce-phone-row">
            <Dropdown v-model="eventGuestPhone.countryCode"
                      :options="countryCodeList"
                      optionValue="value"
                      optionLabel="text"
                      placeholder="Code"
                      filter
                      :disabled="disabled"
                      class="ce-country-dropdown"
                      @change="onEventGuestCountryCodeChange" />

            <input v-model="eventGuestPhone.phoneNumber"
                   type="text"
                   inputmode="numeric"
                   class="ce-phone-input"
                   :disabled="disabled"
                   :maxlength="eventGuestPhoneLength === 10 ? eventGuestPhoneLength + 4 : eventGuestPhoneLength"
                   placeholder="Phone number"
                   @input="onEventGuestPhoneInput" />
        </div>

        <div v-if="eventGuestPhoneError" class="ce-phone-error">
            {{ eventGuestPhoneError }}
        </div>

        <button type="button"
                class="ce-btn ce-btn--primary ce-phone-submit"
                :disabled="disabled || !isEventGuestPhoneComplete"
                @click="submitEventGuestPhone">
            Continue
        </button>
    </div>

    <!-- Event Guest Invitation Method -->
    <div v-else-if="msg.type === 'event_guest_invite_prompt'"
         class="ce-phone-card ce-invite-card">

        <div class="ce-phone-question">
            {{ invitePromptText }}
        </div>

        <div class="ce-invite-choice-list">
            <button type="button"
                    class="ce-invite-choice-btn"
                    :disabled="disabled"
                    @click="$emit('select-event-guest-invite', 'sms')">
                <span>SMS</span>
            </button>

            <button type="button"
                    class="ce-invite-choice-btn"
                    :disabled="disabled"
                    @click="$emit('select-event-guest-invite', 'email')">
                <span>Email</span>
            </button>
        </div>
    </div>

    <!-- Other Event Flow text -->
    <div v-else-if="msg.type === 'event_flow'"
         class="ce-text-message">
        <span v-html="formatEventPhonePrompt(responseText)"></span>
    </div>

    <!-- Event Summary / editable form -->
    <div v-else-if="msg.type === 'event_preview'" class="ce-card ce-card--form">
        <div class="ce-card__header">
            <div>
                <div class="ce-card__title">Event Summary</div>
                <div class="ce-card__sub">
                    {{ msg.payload?.response || 'Please review before creating' }}
                </div>
            </div>
        </div>

        <div class="ce-card__body">

            <!-- Event Title -->
            <div class="ce-field">
                <label>
                    Event Title <span class="ce-required">*</span>
                </label>
                <input v-model="eventForm.title"
                       type="text"
                       class="ce-input"
                       maxlength="200"
                       :disabled="disabled"
                       placeholder="Enter Event Title" />
            </div>
            <div v-if="eventValidationTouched && !String(eventForm.title || '').trim()"
                 class="ce-event-validation-error"
                 style="text-align:left;">
                Event Title is required.
            </div>

            <!-- Access Type -->
            <div class="ce-field">
                <label>Access Type</label>
                <div class="ce-access-type" style="justify-content:flex-start;">
                    <label class="ce-radio-label">
                        <input v-model="eventForm.accessType"
                               type="radio"
                               value="paid"
                               :disabled="disabled"
                               @change="onEventAccessTypeChange" />
                        <span>Paid</span>
                    </label>
                    <label class="ce-radio-label">
                        <input v-model="eventForm.accessType"
                               type="radio"
                               value="unpaid"
                               :disabled="disabled"
                               @change="onEventAccessTypeChange" />
                        <span>Unpaid</span>
                    </label>
                </div>
            </div>

            <!-- Entry Fee (Paid only) -->
            <div v-if="eventForm.accessType === 'paid'" class="ce-field">
                <label>
                    Entry Fee ($) <span class="ce-required">*</span>
                </label>
                <input v-model.number="eventForm.entryFee"
                       type="number"
                       min="0"
                       step="0.01"
                       class="ce-input"
                       :disabled="disabled"
                       placeholder="0.00" />
            </div>

            <!-- Date -->
            <div class="ce-field">
                <label>
                    Date <span class="ce-required">*</span>
                </label>
                <Calendar v-model="eventFormEventDateModel"
                          dateFormat="mm-dd-yy"
                          placeholder="mm-dd-yyyy"
                          :minDate="choreMinDate"
                          :manualInput="false"
                          :disabled="disabled"
                          class="w-100 ce-calendar"
                          inputClass="ce-input w-100"
                          :showIcon="false" />
            </div>
            <div v-if="eventValidationTouched && !eventForm.eventDate"
                 class="ce-event-validation-error"
                 style="text-align:left;">
                Date is required.
            </div>

            <!-- Start Time -->
            <div class="ce-field">
                <label>Start Time</label>
                <input v-model="eventForm.startTime"
                       type="time"
                       class="ce-input"
                       :disabled="disabled" />
            </div>

            <!-- Address section -->
            <div class="ce-addr-title">Address</div>

            <div class="ce-field">
                <label>Street Address 1</label>
                <textarea v-model="eventForm.addressLine1"
                          class="ce-input"
                          rows="2"
                          :disabled="disabled"
                          placeholder="Street Address 1"></textarea>
            </div>

            <div class="ce-field">
                <label>Street Address 2</label>
                <textarea v-model="eventForm.addressLine2"
                          class="ce-input"
                          rows="2"
                          :disabled="disabled"
                          placeholder="Street Address 2"></textarea>
            </div>

            <div class="ce-field">
                <label>Country</label>
                <Dropdown v-model="eventForm.country"
                          :options="countryList"
                          optionValue="value"
                          optionLabel="text"
                          filter
                          showClear
                          placeholder="Select country"
                          class="w-100 ce-dropdown"
                          :disabled="disabled"
                          @change="onCountryChange" />
            </div>

            <div class="ce-field" v-if="eventForm.country === US_COUNTRY_ID">
                <label>State</label>
                <Dropdown v-model="eventForm.state"
                          :options="stateList"
                          optionValue="value"
                          optionLabel="text"
                          filter
                          showClear
                          placeholder="Select state"
                          class="w-100 ce-dropdown"
                          :disabled="disabled" />
            </div>
            <div class="ce-field" v-else>
                <label>State</label>
                <input v-model="eventForm.stateName"
                       type="text"
                       class="ce-input"
                       maxlength="200"
                       :disabled="disabled"
                       placeholder="Enter state" />
            </div>

            <div class="ce-field">
                <label>City</label>
                <input v-model="eventForm.city"
                       type="text"
                       class="ce-input"
                       :disabled="disabled"
                       placeholder="City" />
            </div>

            <div class="ce-field">
                <label>Zip Code</label>
                <input v-model="eventForm.zip"
                       type="text"
                       class="ce-input"
                       maxlength="10"
                       :disabled="disabled"
                       placeholder="Zip code" />
            </div>
        </div>

        <div class="ce-card__actions">
            <button type="button"
                    class="ce-btn ce-btn--primary"
                    :disabled="disabled"
                    @click="onConfirmEvent">
                Confirm &amp; Create
            </button>
            <button type="button"
                    class="ce-btn ce-btn--link"
                    :disabled="disabled"
                    @click="$emit('cancel')">
                Cancel
            </button>
        </div>
    </div>

    <!-- Event created / failed -->
    <div v-else-if="msg.type === 'event_created' || msg.type === 'event_failed'" class="ticket-preview-card">
        <div class="ticket-preview-header" :style="isEventError ? HEADER_ERROR : null">

            <div>
                <h4>{{ isEventError ? 'Event Failed' : 'Event Created! ' }}</h4>
                <p>{{ isEventError ? 'Something went wrong.' : 'Your event is live.' }}</p>
            </div>

        </div>
        <div class="ticket-preview-body">
            <div class="ticket-preview-row"><span class="tp-label">Title</span><span class="tp-value">{{ eventData.title || '—' }}</span></div>
            <div class="ticket-preview-row"><span class="tp-label">Date</span><span class="tp-value">{{ formatDate(eventData.eventDate) }}</span></div>
            <div class="ticket-preview-row"><span class="tp-label">Start Time</span><span class="tp-value">{{ formatTime(eventData.startTime) || '—' }}</span></div>
            <div class="ticket-preview-row"><span class="tp-label">Entry Fee</span><span class="tp-value">${{ Number(eventData.entryFee || 0).toFixed(2) }}</span></div>
            <div v-if="isEventError" class="ticket-preview-error">

                <p><strong>Error:</strong> {{ eventData.error || 'Please try again.' }}</p>
            </div>
        </div>
        <div v-if="!isEventError && eventData.eventId" class="ce-card__pad">
            <button type="button"
                    class="ce-btn ce-btn--secondary"
                    :disabled="disabled"
                    @click="$emit('add-guest', { eventId: eventData.eventId, title: eventData.title })">
                Add Guest
            </button>
        </div>
        <div class="ticket-preview-footer">
            <span> {{ formatMMDDYYYY(currentDate) }}</span>
            <span :style="{ color: isEventError ? '#dc2626' : '#16a34a' }">{{ isEventError ? 'Not Saved' : 'Saved' }}</span>
        </div>
    </div>

    <!-- Event list for Guest List flow -->
    <div v-else-if="msg.type === 'view_events_guestlist'" class="ticket-preview-card">
        <div class="ticket-preview-header">
            <div>
                <h4>{{ eventData.heading || 'Select an Event to View Guest List' }}</h4>
                <p>{{ events.length }} event{{ events.length === 1 ? '' : 's' }} found</p>
            </div>
        </div>

        <div class="ticket-preview-body ce-list-body">
            <div v-if="!events.length" class="ce-empty">No events found.</div>

            <div v-for="(e, idx) in events"
                 :key="e.eventId || idx"
                 class="ce-list-item"
                 :class="{ 'ce-list-item--last': idx === events.length - 1 }">

                <div class="ce-list-item__main">
                    <div class="ce-list-item__title">{{ e.title || 'Event' }}</div>
                    <div class="ce-list-item__meta">
                        {{ formatShortDate(e.eventDate) }}
                        · {{ formatTimeNoSeconds(e.startTime) }}
                        · ${{ Number(e.entryFee || 0).toFixed(2) }}
                    </div>

                    <div class="ce-list-item__tags">
                        <span class="ce-pill"
                              :style="pillStyle(e.isCancelled ? '#dc2626' : '#16a34a')">
                            {{ e.isCancelled ? 'Cancelled' : 'Upcoming' }}
                        </span>
                        <span class="ce-list-item__due">
                            {{ e.confirmed || 0 }}/{{ e.totalSent || 0 }} confirmed
                        </span>
                    </div>
                </div>

                <div class="ce-list-item__actions">
                    <button type="button"
                            class="ce-btn ce-btn--accent-link"
                            :disabled="disabled"
                            @click="$emit('event-detail', e)">
                        Details
                    </button>

                    <button type="button"
                            class="ce-btn ce-btn--success-link"
                            :disabled="disabled || e.isCancelled"
                            @click="$emit('add-guest', e)">
                        Add Guest
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- Event list -->
    <div v-else-if="msg.type === 'view_events'" class="ticket-preview-card">
        <div class="ticket-preview-header">

            <div><h4>{{ eventData.heading || 'Your Events' }}</h4></div>
        </div>
        <div class="ticket-preview-body ce-list-body">
            <div v-if="!events.length" class="ce-empty">No events found.</div>
            <div v-for="(e, idx) in events" :key="e.eventId || idx" class="ce-list-item" :class="{ 'ce-list-item--last': idx === events.length - 1 }">
                <div class="ce-list-item__main">
                    <div class="ce-list-item__title">{{ e.title }}</div>
                    <div class="ce-list-item__meta"> {{ formatShortDate(e.eventDate) }} · {{ formatTimeNoSeconds(e.startTime) }} · ${{ Number(e.entryFee || 0).toFixed(2) }}</div>
                    <div class="ce-list-item__tags">
                        <span class="ce-pill"
                              :style="pillStyle(
                                  e.isCancelled
                                      ? '#dc2626'
                                      : (isPastEvent(e) ? '#64748b' : '#16a34a')
                              )">
                            {{ e.isCancelled ? 'Cancelled' : (isPastEvent(e) ? 'Past' : 'Upcoming') }}
                        </span>
                        <span class="ce-list-item__due"> {{ e.confirmed }}/{{ e.totalSent }} confirmed</span>
                    </div>
                </div>
                <div class="ce-list-item__actions">
                    <button type="button"
                            class="ce-btn ce-btn--accent-link"
                            :disabled="disabled"
                            @click="$emit('event-detail', e)">
                        Details &#x25BC;
                    </button>

                    <button v-if="!isPastEvent(e)"
                            type="button"
                            class="ce-btn ce-btn--success-link"
                            :disabled="disabled || e.isCancelled"
                            @click="$emit('add-guest', e)">
                        Add Guest
                    </button>

                    <button v-if="!isPastEvent(e)"
                            type="button"
                            class="ce-btn ce-btn--warn-link"
                            :disabled="disabled"
                            @click="$emit('cancel-event', e)">
                        {{ e.isCancelled ? 'Reopen' : 'Cancel' }}
                    </button>

                    <button type="button"
                            class="ce-btn ce-btn--danger-link"
                            :disabled="disabled"
                            @click="$emit('delete-event', e)">
                        Delete
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- Duplicate Event List -->
    <div v-else-if="msg.type === 'view_events_duplicate'" class="ticket-preview-card">
        <div class="ticket-preview-header">
            <div>
                <h4>{{ eventData.heading || 'Select an Event to Duplicate' }}</h4>
            </div>
            <span class="ticket-preview-number">
                {{ events.length }} event{{ events.length === 1 ? '' : 's' }}
            </span>
        </div>

        <div class="ticket-preview-body ce-list-body">
            <div v-if="!events.length" class="ce-empty">
                No events found.
            </div>

            <div v-for="(e, idx) in events"
                 :key="e.eventId || idx"
                 class="ce-list-item"
                 :class="{ 'ce-list-item--last': idx === events.length - 1 }">

                <div class="ce-list-item__main">
                    <div class="ce-list-item__title">{{ e.title }}</div>

                    <div class="ce-list-item__meta">
                        {{ formatShortDate(e.eventDate) }}
                        · {{ formatTimeNoSeconds(e.startTime) }}
                        · ${{ Number(e.entryFee || 0).toFixed(2) }}
                    </div>

                    <div class="ce-list-item__tags">
                        <span class="ce-pill"
                              :style="pillStyle(e.isCancelled ? '#dc2626' : '#16a34a')">
                            {{ e.isCancelled ? 'Cancelled' : 'Upcoming' }}
                        </span>

                        <span class="ce-list-item__due">
                            {{ e.confirmed }}/{{ e.totalSent }} confirmed
                        </span>
                    </div>
                </div>

                <div class="ce-list-item__actions">
                    <button type="button"
                            class="ce-duplicate-btn"
                            :disabled="disabled"
                            @click="$emit('duplicate-event', e)">
                        Duplicate
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- Event detail -->
    <div v-else-if="msg.type === 'event_detail'"
         class="ticket-preview-card ce-event-detail-card">
        <div class="ticket-preview-header">

            <div>
                <h4>{{ eventDetail.title || 'Event' }}</h4>
                <p>{{ eventDetail.message || '' }}</p>
            </div>

            <span v-if="isPastEvent(eventDetail)"
                  class="ce-event-detail-status ce-event-detail-status--past">
                Past
            </span>
        </div>
        <div class="ticket-preview-body">
            <div class="ticket-preview-row"><span class="tp-label">Date</span><span class="tp-value">{{ formatDate(eventDetail.eventDate) }}</span></div>
            <div class="ticket-preview-row"><span class="tp-label">Time</span><span class="tp-value">{{ formatTimeNoSeconds(eventDetail.startTime) }}</span></div>
            <div class="ticket-preview-row"><span class="tp-label">Entry Fee</span><span class="tp-value">${{ Number(eventDetail.entryFee || 0).toFixed(2) }}</span></div>
            <div class="ticket-preview-row"><span class="tp-label">Address</span><span class="tp-value">{{ eventAddress }}</span></div>
            <div class="ce-guest-heading">GUESTS ({{ guests.length }})</div>
            <div v-if="!guests.length" class="ce-empty">No guests yet.</div>
            <div v-for="(g, gi) in guests" :key="gi" class="ce-guest-row">
                <div class="ce-guest-info">
                    <div class="ce-guest-name">{{ g.fullName }}</div>

                    <div v-if="g.email"
                         class="ce-guest-contact ce-guest-email">
                        {{ g.email }}
                    </div>

                    <div v-if="g.phone"
                         class="ce-guest-contact ce-guest-phone">
                        {{ formatGuestPhone(g.phone) }}
                    </div>
                    <div class="ce-list-item__tags">
                        <span class="ce-pill" :style="pillStyle(eventRsvpColor(g.rsvp))">{{ g.rsvp }}</span>
                        <span class="ce-pill" :style="pillStyle('#0369a1')">{{ g.payment }}</span>
                    </div>
                </div>


                <div class="ce-guest-row-actions">


                    <button type="button"
                            class="ce-guest-delete-icon"
                            :disabled="disabled || !g.guestId"
                            title="Delete Guest"
                            aria-label="Delete Guest"
                            @click="requestEventGuestDelete(g)">
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                    </button>
                </div>
            </div>
        </div>
        <div v-if="!isPastEvent(eventDetail)"
             class="ce-card__pad ce-event-detail-actions">
            <button type="button"
                    class="ce-btn ce-btn--secondary ce-event-detail-add-guest"
                    :disabled="disabled"
                    @click="$emit('add-guest', { eventId: eventDetail.eventId, title: eventDetail.title })">
                Add Guest
            </button>
        </div>
    </div>
    <!-- Small centered Chore Status popup -->
    <div v-if="choreStatusModal.show"
         class="ce-chore-status-popup-overlay"
         @click.self="closeQuickChoreStatusPopup">

        <div class="ce-chore-status-popup-card" @click.stop>
            <div class="ce-chore-status-popup-header">
                Update Status
            </div>

            <div class="ce-chore-status-popup-body">
                <div class="ce-chore-status-popup-title">
                    {{ choreStatusModal.title }}
                </div>

                <div class="ce-chore-status-popup-field">
                    <label class="ce-chore-status-popup-label">
                        Status:<span class="ce-chore-status-required">*</span>
                    </label>

                    <select v-model="choreStatusModal.selectedStatus"
                            class="ce-input ce-select"
                            :disabled="disabled">
                        <option value="" disabled>Select Status</option>
                        <option v-for="opt in quickChoreStatusDropdownOptions"
                                :key="opt.value"
                                :value="opt.value">
                            {{ opt.text }}
                        </option>
                    </select>
                </div>

                <div class="ce-chore-status-popup-actions">
                    <button type="button"
                            class="ce-chore-status-popup-btn ce-chore-status-popup-ok"
                            :disabled="
                                disabled ||
                                !choreStatusModal.selectedStatus ||
                                String(choreStatusModal.selectedStatus).toLowerCase() ===
                                String(choreStatusModal.currentStatus).toLowerCase()
                            "
                            @click="confirmQuickChoreStatusChange">
                        Save
                    </button>

                    <button type="button"
                            class="ce-chore-status-popup-btn ce-chore-status-popup-cancel"
                            @click="closeQuickChoreStatusPopup">
                        Close
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- Event Guest Remove Confirmation Popup -->
    <div v-if="eventGuestDeleteModal.show"
         class="ce-event-guest-confirm-overlay"
         @click.self="closeEventGuestDeleteModal">

        <div class="ce-event-guest-confirm-card" @click.stop>
            <div class="ce-event-guest-confirm-header">
                Remove Guest
            </div>

            <div class="ce-event-guest-confirm-body">
                <p class="ce-event-guest-confirm-message">
                    Are you sure you want to remove "{{ eventGuestDeleteModal.guestName }}" from this event?
                </p>

                <div class="ce-event-guest-confirm-actions">
                    <button type="button"
                            class="ce-event-guest-confirm-btn ce-event-guest-confirm-ok"
                            @click="confirmEventGuestDelete">
                        Ok
                    </button>

                    <button type="button"
                            class="ce-event-guest-confirm-btn ce-event-guest-confirm-cancel"
                            @click="closeEventGuestDeleteModal">
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- Chore Remove Confirmation Popup -->
    <div v-if="choreDeleteModal.show"
         class="ce-confirm-overlay"
         @click.self="closeChoreDeleteModal">

        <div class="ce-confirm-card" @click.stop>
            <div class="ce-confirm-header">
                Delete Chore
            </div>

            <div class="ce-confirm-body">
                <p class="ce-confirm-message">
                    Are you sure you want to delete chore "{{ choreDeleteModal.title }}"?
                </p>

                <div class="ce-confirm-actions">
                    <button type="button"
                            class="ce-confirm-btn ce-confirm-ok"
                            @click="confirmChoreDelete">
                        Ok
                    </button>

                    <button type="button"
                            class="ce-confirm-btn ce-confirm-cancel"
                            @click="closeChoreDeleteModal">
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Dropdown from 'primevue/dropdown';
    import Calendar from 'primevue/calendar';
    import UserService from '@/service/user.service.js';
    import axios from 'axios';
    import {
        findDefaultUsCountryCode,
        resolvePhoneLength,
    } from '@/helpers/phoneCountryHelpers.js';
    import {
        formatChoresEventsDate,
        formatChoresEventsShortDate,
        formatChoresEventsTime,
        choreStatusColor,
        eventRsvpColor,
        joinNames,
        buildEventAddress,
    } from './choresEvents.helpers';

    const US_COUNTRY_ID = '5c94ae54-5519-4004-becd-3366bbd6e1a9';
    const HEADER_ERROR = 'background:linear-gradient(135deg,#991b1b,#dc2626);';

    // Uses the configured Web API base for Local / Stage / Live.
    // Falls back to the current Spliyt API only when env config is missing.
    const WEB_API_BASE = String(
        process.env.VUE_APP_WebApiBaseUrl ||
        'https://api.spliyt.com/apigateway'
    ).replace(/\/+$/, '');

    function formatMMDDYYYY(value) {
        if (!value) return '—';
        const text = String(value).trim();
        const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (iso) return `${iso[2]}-${iso[3]}-${iso[1]}`;
        const date = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
        if (date) return `${date[1].padStart(2, '0')}-${date[2].padStart(2, '0')}-${date[3]}`;
        return text;
    }

    function emptyChoreForm() {
        return {
            choreTitle: '',
            frequency: '',
            roommateIds: [],
            roommateNames: [],
            dueDate: '',
            weekDays: [],
            notes: '',
        };
    }

    function emptyEventForm() {
        return {
            title: '',
            accessType: 'unpaid',
            entryFee: null,
            eventDate: '',
            startTime: '',
            addressLine1: '',
            addressLine2: '',
            country: US_COUNTRY_ID,
            state: null,
            stateName: '',
            city: '',
            zip: '',
            message: '',
            companyId: null,
            actorUserId: null,
        };
    }

    export default {
        name: 'ChatbotChoresEvents',
        components: {
            Dropdown,
            Calendar,
        },
        props: {
            msg: { type: Object, required: true },
            disabled: { type: Boolean, default: false },
            currentDate: { type: String, default: '' },
        },
        emits: [
            'confirm-chore',
            'delete-chore',
            'confirm-event',
            'cancel',
            'add-guest',
            'event-detail',
            'cancel-event',
            'delete-event',
            'delete-event-guest',
            'duplicate-event',
            'select-event-guest-invite',
            'select-event-guest-source',
            'select-event-guest-roommate',
            'event-guest-phone-validity-change',
            'select-chore-frequency',
            'select-chore-weekdays',
            'select-chore-roommates',
            'select-chore-status-update',
            'quick-chore-status-change',
            'submit-event-guest-phone',
        ],
        data() {
            return {
                US_COUNTRY_ID,
                HEADER_ERROR,
                choreForm: emptyChoreForm(),
                choreValidationTouched: false,
                _choreFormInitKey: '',

                // My Chores API synchronization.
                // null = API not loaded/failed -> keep existing chatbot payload.
                // []   = API loaded successfully and no chores were returned.
                syncedChores: null,
                myChoresLoading: false,

                eventForm: emptyEventForm(),
                countryList: [],
                stateList: [],
                selectedChoreRoommateIds: [],
                selectedChoreWeekDays: [],
                choreWeekdayOptions: [
                    { label: 'M', text: 'Monday', value: 'Mon' },
                    { label: 'T', text: 'Tuesday', value: 'Tue' },
                    { label: 'W', text: 'Wednesday', value: 'Wed' },
                    { label: 'T', text: 'Thursday', value: 'Thu' },
                    { label: 'F', text: 'Friday', value: 'Fri' },
                    { label: 'S', text: 'Saturday', value: 'Sat' },
                    { label: 'S', text: 'Sunday', value: 'Sun' },
                ],
                expandedChoreKey: '',
                _formInitKey: '',
                _addressLoadedKey: '',

                countryCodeList: [],
                eventGuestPhone: {
                    countryCode: '',
                    phoneNumber: '',
                },
                eventGuestPhoneLength: 10,
                eventGuestPhoneError: '',

                selectedEventGuestSource: '',
                selectedEventGuestRoommateId: '',
                selectedEventGuestRoommateIds: [],

                eventValidationTouched: false,

                choreDeleteModal: {
                    show: false,
                    title: '',
                    chore: null,
                },

                choreStatusModal: {
                    show: false,
                    chore: null,
                    title: '',
                    currentStatus: '',
                    selectedStatus: '',
                },

                eventGuestDeleteModal: {
                    show: false,
                    eventId: '',
                    guestId: '',
                    guestName: '',
                },
            };
        },
        async created() {
            await this.GetCountryCodeList();
        },
        computed: {
            isBoatAsset() {
                const t = String(localStorage.getItem('assetType') || '').toLowerCase();
                return t === 'boat';
            },
            /** Boat → Task; Household (and others) → Chore */
            lblChore() {
                return this.isBoatAsset ? 'Task' : 'Chore';
            },
            lblChoreLower() {
                return this.lblChore.toLowerCase();
            },
            lblChorePlural() {
                return this.isBoatAsset ? 'Tasks' : 'Chores';
            },
            /** Boat portal: Assign Co-owner; Household: Assign Roommate */
            lblAssignPerson() {
                return this.isBoatAsset ? 'Assign Co-owner' : 'Assign Roommate';
            },
            lblAssignPersonPlural() {
                return this.isBoatAsset ? 'Assign Co-owners' : 'Assign Roommates';
            },
            lblChoreTitle() {
                return this.isBoatAsset ? 'Task Title' : 'Chore Title';
            },
            lblFrequency() {
                return this.isBoatAsset ? 'Task Frequency' : 'Chore Frequency';
            },
            /** Today at midnight — past dates not selectable */
            choreMinDate() {
                const d = new Date();
                d.setHours(0, 0, 0, 0);
                return d;
            },
            /** Date object for PrimeVue Calendar; stores YYYY-MM-DD in choreForm.dueDate */
            choreFormDueDateModel: {
                get() {
                    const raw = String(this.choreForm?.dueDate || '').trim();
                    if (!raw) return null;
                    // YYYY-MM-DD
                    const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
                    if (iso) {
                        const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
                        return isNaN(d.getTime()) ? null : d;
                    }
                    const d = new Date(raw);
                    return isNaN(d.getTime()) ? null : d;
                },
                set(val) {
                    if (!val) {
                        this.choreForm.dueDate = '';
                        return;
                    }
                    const d = val instanceof Date ? val : new Date(val);
                    if (isNaN(d.getTime())) {
                        this.choreForm.dueDate = '';
                        return;
                    }
                    const yyyy = d.getFullYear();
                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                    const dd = String(d.getDate()).padStart(2, '0');
                    this.choreForm.dueDate = `${yyyy}-${mm}-${dd}`;
                },
            },

            /** Date object for PrimeVue Calendar; stores YYYY-MM-DD in eventForm.eventDate */
            eventFormEventDateModel: {
                get() {
                    const raw = String(this.eventForm?.eventDate || '').trim();
                    if (!raw) return null;
                    const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
                    if (iso) {
                        const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
                        return isNaN(d.getTime()) ? null : d;
                    }
                    const d = new Date(raw);
                    return isNaN(d.getTime()) ? null : d;
                },
                set(val) {
                    if (!val) {
                        this.eventForm.eventDate = '';
                        return;
                    }
                    const d = val instanceof Date ? val : new Date(val);
                    if (isNaN(d.getTime())) {
                        this.eventForm.eventDate = '';
                        return;
                    }
                    const yyyy = d.getFullYear();
                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                    const dd = String(d.getDate()).padStart(2, '0');
                    this.eventForm.eventDate = `${yyyy}-${mm}-${dd}`;
                },
            },

            responseText() {
                return this.msg?.payload?.response || this.msg?.text || '';
            },

            choreWeekdaysPromptText() {
                return String(this.responseText || '')
                    .replace(/<br\s*\/?>/gi, ' ')
                    .replace(/<[^>]*>/g, ' ')
                    .replace(/\*\*/g, '')
                    .replace(/\s+/g, ' ')
                    .trim() || 'Which days should this chore repeat on?';
            },

            invitePromptText() {
                return String(this.responseText || '')
                    .replace(/\*\*/g, '')
                    .trim() || 'Please choose Email or SMS to send the invitation.';
            },
            eventGuestPromptText() {
                return String(this.responseText || '')
                    .replace(/<br\s*\/?>/gi, ' ')
                    .replace(/<[^>]*>/g, ' ')
                    .replace(/\*\*/g, '')
                    .replace(/\s+/g, ' ')
                    .trim();
            },

            isEventGuestSourcePrompt() {
                if (this.msg?.type === 'event_guest_source_prompt') return true;
                if (this.msg?.type !== 'event_flow') return false;

                const text = this.eventGuestPromptText.toLowerCase();

                return (
                    (
                        text.includes('adding a guest') ||
                        text.includes('add a guest')
                    ) &&
                    text.includes('roommate') &&
                    (
                        text.includes('another person') ||
                        text.includes('another guest') ||
                        text.includes('add another')
                    )
                );
            },

            isEventGuestRoommatePrompt() {
                if (this.msg?.type === 'event_guest_roommate_prompt') return true;
                if (this.msg?.type !== 'event_flow') return false;

                const text = this.eventGuestPromptText.toLowerCase();

                return (
                    (text.includes('which roommate') || text.includes('which member')) &&
                    (
                        text.includes('invite') ||
                        text.includes('add')
                    )
                );
            },

            eventGuestSourceOptions() {
                return [
                    { text: this.isBoatAsset ? 'Members' : 'Roommates', value: 'roommate' },
                    { text: 'Another Person', value: 'other' },
                ];
            },

            eventGuestRoommateOptions() {
                const data = this.msg?.payload?.event_data || {};

                return Array.isArray(data.options)
                    ? data.options
                    : [];
            },

            eventGuestRoommateDropdownOptions() {
                return this.eventGuestRoommateOptions.map((roommate, index) => ({
                    text: this.getChoreRoommateDisplayName(roommate) ||
                        roommate.name ||
                        roommate.text ||
                        roommate.label ||
                        roommate.fullName ||
                        `${this.isBoatAsset ? 'Member' : 'Roommate'} ${index + 1}`,
                    value: String(
                        roommate.id ||
                        roommate.value ||
                        roommate.userId ||
                        roommate.UserId ||
                        index
                    ),
                    original: roommate,
                }));
            },

            allEventGuestRoommatesSelected() {
                const opts = this.eventGuestRoommateDropdownOptions || [];
                if (!opts.length) return false;
                return opts.every(r =>
                    this.selectedEventGuestRoommateIds.includes(String(r.value))
                );
            },

            isEventGuestPhoneComplete() {
                const digits = String(
                    this.eventGuestPhone.phoneNumber || ''
                ).replace(/\D/g, '');

                return digits.length === Number(this.eventGuestPhoneLength || 10);
            },

            isEventGuestPhonePrompt() {
                if (this.msg?.type !== 'event_flow') return false;

                const text = String(this.responseText || '')
                    .replace(/<[^>]*>/g, ' ')
                    .replace(/\*\*/g, '')
                    .toLowerCase();

                return text.includes('phone number')
                    || text.includes('phone no')
                    || text.includes("guest's phone")
                    || text.includes('guest’s phone');
            },
            choreStatusUpdateQuestion() {
                const text = String(this.responseText || '')
                    .replace(/<br\s*\/?>/gi, '\n')
                    .replace(/<[^>]*>/g, '')
                    .replace(/\*\*/g, '')
                    .trim();

                const lines = text
                    .split(/\r?\n/)
                    .map(line => line.trim())
                    .filter(Boolean);

                if (!lines.length) {
                    return 'Select an option';
                }

                // The backend response normally puts the question first.
                return lines[0];
            },

            choreStatusUpdateOptions() {
                const text = String(this.responseText || '')
                    .replace(/<br\s*\/?>/gi, '\n')
                    .replace(/<[^>]*>/g, '')
                    .replace(/\*\*/g, '')
                    .trim();

                const lines = text
                    .split(/\r?\n/)
                    .map(line => line.trim())
                    .filter(Boolean);

                // STEP 1:
                // "Which chore would you like to update?"
                // "1. plant water — currently Completed"
                const numbered = lines
                    .slice(1)
                    .map(line => {
                        const match = line.match(/^\s*(\d+)\s*[.)-]\s*(.+)$/);
                        if (!match) return null;

                        return {
                            value: match[1],
                            label: `${match[1]}. ${match[2].trim()}`
                        };
                    })
                    .filter(Boolean);

                if (numbered.length) {
                    return numbered;
                }

                // STEP 2:
                // MyChores currently uses only the statuses required here:
                // Completed and Pending.
                // Keep STEP 1 chore selection unchanged.
                const allowedStatuses = ['Completed', 'Pending'];

                const statusOptions = allowedStatuses
                    .filter(status =>
                        new RegExp(`\\b${status}\\b`, 'i').test(text)
                    )
                    .map(status => ({
                        value: status,
                        label: status
                    }));

                if (statusOptions.length) {
                    return statusOptions;
                }

                // Safe fallback for comma-separated backend options.
                // Only Completed/Pending are allowed in this status picker.
                const parenthesized = text.match(/\(([^()]+)\)\s*$/);
                if (parenthesized) {
                    return parenthesized[1]
                        .split(',')
                        .map(item => item.trim())
                        .filter(item =>
                            allowedStatuses.some(
                                status => status.toLowerCase() === item.toLowerCase()
                            )
                        )
                        .map(item => {
                            const matched = allowedStatuses.find(
                                status => status.toLowerCase() === item.toLowerCase()
                            );

                            return {
                                value: matched,
                                label: matched
                            };
                        });
                }

                // FIX:
                // Some backend responses return only:
                // "What status should <chore> be set to?"
                // without appending the status list.
                // In that case show the same supported status choices
                // instead of "No options available."
                if (/what\s+status\s+should.+be\s+set\s+to\??/i.test(text)) {
                    return allowedStatuses.map(status => ({
                        value: status,
                        label: status
                    }));
                }

                return [];
            },

            chorePre() {
                return this.msg?.payload?.chore_data?.prefilled || {};
            },

            choreFrequencyFormOptions() {
                return [
                    { text: 'One Time', value: 'One Time' },
                    { text: 'Daily', value: 'Daily' },
                    { text: 'Weekly', value: 'Weekly' },
                    { text: 'Bi-Weekly', value: 'Bi-Weekly' },
                    { text: 'Monthly', value: 'Monthly' },
                ];
            },

            isWeeklyChoreFrequency() {
                return String(this.choreForm.frequency || '').toLowerCase() === 'weekly';
            },

            choreFormWeekDaysLabel() {
                return this.choreForm.weekDays.length
                    ? this.choreForm.weekDays.join(', ')
                    : 'Select Week Days';
            },

            allChoreFormWeekDaysSelected() {
                return this.choreWeekdayOptions.length > 0 &&
                    this.choreWeekdayOptions.every(day =>
                        this.choreForm.weekDays.includes(day.value)
                    );
            },

            choreFormRoommateOptions() {
                const pre = this.chorePre || {};
                const data = this.msg?.payload?.chore_data || {};

                const rawOptions =
                    (Array.isArray(data.options) && data.options.length
                        ? data.options
                        : null) ||
                    (Array.isArray(data.roommates) && data.roommates.length
                        ? data.roommates
                        : null) ||
                    (Array.isArray(pre.roommates) && pre.roommates.length
                        ? pre.roommates
                        : []);

                const mapped = rawOptions.map((roommate, index) => ({
                    text: this.getChoreRoommateDisplayName(roommate) ||
                        roommate.name ||
                        roommate.text ||
                        roommate.label ||
                        roommate.fullName ||
                        roommate.userName ||
                        `${this.isBoatAsset ? 'Co-owner' : 'Roommate'} ${index + 1}`,
                    value: String(
                        roommate.id ||
                        roommate.value ||
                        roommate.userId ||
                        roommate.UserId ||
                        ''
                    ),
                    original: roommate,
                })).filter(item => item.value);

                // Keep the already selected roommate available even when
                // the preview payload does not return the full roommate list.
                const currentIds =
                    Array.isArray(pre.roommateIds) ? pre.roommateIds :
                        Array.isArray(pre.assignedUserIds) ? pre.assignedUserIds :
                            pre.roommateId ? [pre.roommateId] :
                                pre.assignedUserId ? [pre.assignedUserId] :
                                    [];

                const currentNames =
                    Array.isArray(pre.roommateNames) ? pre.roommateNames :
                        pre.roommateName ? [pre.roommateName] :
                            pre.assignedUserName ? [pre.assignedUserName] :
                                [];

                currentIds.forEach((id, index) => {
                    const currentId = String(id || '');
                    const exists = mapped.some(item => String(item.value) === currentId);

                    if (currentId && !exists) {
                        mapped.push({
                            text: currentNames[index] || `Selected Roommate ${index + 1}`,
                            value: currentId,
                            original: null,
                        });
                    }
                });

                return mapped;
            },

            choreData() {
                return this.msg?.payload?.chore_data || {};
            },
            chores() {
                const source = Array.isArray(this.syncedChores)
                    ? this.syncedChores
                    : (this.choreData.chores || []);

                // Keep status-filtered Genie lists filtered after MyChores sync.
                const heading = String(this.choreData?.heading || '').toLowerCase();

                if (heading.includes('completed')) {
                    return source.filter(
                        item => String(item?.status || '').toLowerCase() === 'completed'
                    );
                }

                if (heading.includes('pending')) {
                    return source.filter(
                        item => String(item?.status || '').toLowerCase() === 'pending'
                    );
                }

                return source;
            },

            quickChoreStatusOptions() {
                return ['Completed', 'Pending'];
            },

            quickChoreStatusDropdownOptions() {
                return [
                    { text: 'Pending', value: 'Pending' },
                    { text: 'Completed', value: 'Completed' },
                ];
            },
            isChoreError() {
                return this.msg?.type === 'chore_failed';
            },

            choreRoommateOptions() {
                return this.msg?.payload?.chore_data?.options || [];
            },

            allChoreRoommatesSelected() {
                if (!this.choreRoommateOptions.length) return false;

                const allIds = this.choreRoommateOptions.map((roommate) =>
                    String(roommate.id || roommate.value)
                );

                return allIds.every((id) =>
                    this.selectedChoreRoommateIds.includes(id)
                );
            },

            eventPre() {
                return this.msg?.payload?.event_data?.prefilled || {};
            },
            eventData() {
                return this.msg?.payload?.event_data || {};
            },
            events() {
                return this.eventData.events || [];
            },
            isEventError() {
                return this.msg?.type === 'event_failed';
            },
            eventDetail() {
                return this.eventData.detail || {};
            },
            guests() {
                return this.eventData.guests || [];
            },
            eventAddress() {
                return buildEventAddress(this.eventDetail);
            },
        },
        watch: {
            msg: {
                immediate: true,
                deep: true,
                handler(m) {
                    if (
                        m?.type === 'view_chores' ||
                        m?.type === 'chore_status_update_flow'
                    ) {
                        this.loadMyChores();
                    }

                    if (m?.type === 'chore_form') {
                        this.initChoreFormFromPayload();
                    }

                    if (m?.type === 'chore_weekday_options') {
                        const selected = m?.payload?.chore_data?.selected || [];
                        this.selectedChoreWeekDays = Array.isArray(selected)
                            ? selected.map(day => String(day))
                            : [];
                    }

                    if (m?.type === 'event_preview') {
                        this.initEventFormFromPayload();
                        this.loadEventAddressLikePortal();
                    }
                    if (m?.type === 'chore_roommate_options') {
                        this.selectedChoreRoommateIds = [];
                    }

                    if (
                        m?.type === 'event_flow' ||
                        m?.type === 'event_guest_source_prompt' ||
                        m?.type === 'event_guest_roommate_prompt'
                    ) {
                        if (this.isEventGuestSourcePrompt) {
                            this.selectedEventGuestSource = '';
                        }

                        if (this.isEventGuestRoommatePrompt) {
                            // Reset only when a different list of people arrives
                            const key = this.eventGuestRoommateDropdownOptions.map(r => r.value).join('|');
                            if (key !== this.eventGuestOptionsKey) {
                                this.eventGuestOptionsKey = key;
                                this.selectedEventGuestRoommateId = '';
                                this.selectedEventGuestRoommateIds = [];
                            }
                        }
                    }
                },
            },
        },
        methods: {
            joinNames,
            choreStatusColor,
            eventRsvpColor,
            formatDate: formatMMDDYYYY,
            formatShortDate: formatMMDDYYYY,
            formatMMDDYYYY,

            normalizeMyChore(row) {
                return {
                    id:
                        row?.SchedulerChoreId ??
                        row?.schedulerChoreId ??
                        row?.ChoreId ??
                        row?.choreId ??
                        row?.Id ??
                        row?.id ??
                        '',

                    schedulerChoreId:
                        row?.SchedulerChoreId ??
                        row?.schedulerChoreId ??
                        null,

                    choreId:
                        row?.ChoreId ??
                        row?.choreId ??
                        null,

                    title:
                        row?.Title ??
                        row?.title ??
                        'Chore',

                    description:
                        row?.Description ??
                        row?.description ??
                        null,

                    rotationType:
                        row?.RotationType ??
                        row?.rotationType ??
                        '',

                    frequency:
                        row?.Frequency ??
                        row?.frequency ??
                        '',

                    weekDays:
                        row?.WeekDays ??
                        row?.weekDays ??
                        row?.weekdays ??
                        '',

                    //priority:
                    //    row?.Priority ??
                    //    row?.priority ??
                    //    '',

                    status:
                        row?.Status ??
                        row?.status ??
                        'Pending',

                    startDate:
                        row?.StartDate ??
                        row?.startDate ??
                        null,

                    dueDate:
                        row?.DueDate ??
                        row?.dueDate ??
                        null,

                    assignedUserName:
                        row?.AssignedUserName ??
                        row?.assignedUserName ??
                        '',

                    totalRecord:
                        row?.TotalRecord ??
                        row?.totalRecord ??
                        null,
                };
            },

            async loadMyChores() {
                if (this.myChoresLoading) {
                    return;
                }

                this.myChoresLoading = true;

                try {
                    const token =
                        localStorage.getItem('access_token') ||
                        localStorage.getItem('token') ||
                        '';

                    let storedUserInfo = {};
                    try {
                        storedUserInfo = JSON.parse(
                            localStorage.getItem('userinfo') || '{}'
                        );
                    } catch (e) {
                        storedUserInfo = {};
                    }

                    const storedUserData = storedUserInfo?.data || {};

                    const userId =
                        localStorage.getItem('userId') ||
                        storedUserData.id ||
                        storedUserData.userId ||
                        '';

                    const companyId =
                        localStorage.getItem('companyId') ||
                        storedUserData.companyId ||
                        '';

                    // Same POST search model pattern used by the portal list.
                    // No filter is applied here because chatbot "Chores list"
                    // should retrieve the user's full My Chores list.
                    const requestModel = {
                        searchItem: null,
                        status: '',
                        //priority: '',
                        frequency: '',
                        fromDate: null,
                        toDate: null,
                        sortColumn: 'DueDate',
                        sortDir: 'asc',
                        pageIndex: 0,
                        pageSize: 100,
                        userId,
                        companyId,
                    };

                    const response = await axios.post(
                        `${WEB_API_BASE}/Chore/MyChores`,
                        requestModel,
                        {
                            headers: token
                                ? { Authorization: `Bearer ${token}` }
                                : {}
                        }
                    );

                    const root = response?.data;

                    // Support all response wrappers used by local/stage/live.
                    const rows =
                        Array.isArray(root?.data)
                            ? root.data
                            : Array.isArray(root?.Data)
                                ? root.Data
                                : Array.isArray(root?.result?.data)
                                    ? root.result.data
                                    : Array.isArray(root?.result?.Data)
                                        ? root.result.Data
                                        : Array.isArray(root?.Result?.data)
                                            ? root.Result.data
                                            : Array.isArray(root?.Result?.Data)
                                                ? root.Result.Data
                                                : Array.isArray(root)
                                                    ? root
                                                    : [];

                    const normalizedRows = rows.map(this.normalizeMyChore);

                    /*
                     * Important:
                     * Do not replace a valid Genie chore list with [] just
                     * because the direct MyChores call returns an empty wrapper.
                     * This was the reason the chatbot showed "0 chores" while
                     * the portal showed records.
                     */
                    if (normalizedRows.length > 0) {
                        this.syncedChores = normalizedRows;
                    } else {
                        const existingChores = Array.isArray(this.choreData?.chores)
                            ? this.choreData.chores
                            : [];

                        this.syncedChores = existingChores.length > 0
                            ? null
                            : [];
                    }
                } catch (error) {
                    // Keep existing Genie chore payload as fallback.
                    this.syncedChores = null;
                    console.error(
                        'ChatbotChoresEvents MyChores load failed',
                        error
                    );
                } finally {
                    this.myChoresLoading = false;
                }
            },

            initChoreFormFromPayload() {
                const pre = this.chorePre || {};

                const key = [
                    pre.choreTitle || pre.title || '',
                    pre.frequency || '',
                    pre.dueDate || pre.startDate || '',
                    pre.weekDays || pre.WeekDays || pre.weekdays || '',
                    Array.isArray(pre.roommateIds)
                        ? pre.roommateIds.join(',')
                        : Array.isArray(pre.assignedUserIds)
                            ? pre.assignedUserIds.join(',')
                            : (pre.roommateId || pre.assignedUserId || ''),
                    pre.notes || '',
                ].join('|');

                if (key === this._choreFormInitKey && this.choreForm.choreTitle) {
                    return;
                }

                this._choreFormInitKey = key;
                this.choreValidationTouched = false;

                const roommateIds =
                    Array.isArray(pre.roommateIds) ? pre.roommateIds :
                        Array.isArray(pre.assignedUserIds) ? pre.assignedUserIds :
                            pre.roommateId ? [pre.roommateId] :
                                pre.assignedUserId ? [pre.assignedUserId] :
                                    [];

                const roommateNames =
                    Array.isArray(pre.roommateNames) ? pre.roommateNames :
                        pre.roommateName ? [pre.roommateName] :
                            pre.assignedUserName ? [pre.assignedUserName] :
                                [];

                const rawDate = String(pre.dueDate || pre.startDate || '');

                this.choreForm = {
                    ...emptyChoreForm(),
                    choreTitle: pre.choreTitle || pre.title || '',
                    frequency: pre.frequency || '',
                    roommateIds: roommateIds.filter(id => id != null && String(id).trim()).map(String),
                    roommateNames: roommateNames.map(String),
                    dueDate: rawDate ? rawDate.slice(0, 10) : '',
                    weekDays: this.normalizeChoreWeekDays(
                        pre.weekDays || pre.WeekDays || pre.weekdays
                    ).split(',').filter(Boolean),
                    notes: pre.notes || '',
                };
            },

            toggleAllChoreFormRoommates(checked) {
                if (this.disabled) return;
                this.choreForm.roommateIds = checked
                    ? this.choreFormRoommateOptions.map(item => item.value)
                    : [];
                this.onChoreFormRoommateChange();
            },

            onChoreFrequencyChange() {
                if (!this.isWeeklyChoreFrequency) {
                    this.choreForm.weekDays = [];
                }
            },

            toggleAllChoreFormWeekDays(checked) {
                if (this.disabled) return;

                this.choreForm.weekDays = checked
                    ? this.choreWeekdayOptions.map(day => day.value)
                    : [];
            },

            toggleChoreWeekDay(day) {
                if (this.disabled || !day) return;

                const index = this.selectedChoreWeekDays.indexOf(day);
                if (index >= 0) {
                    this.selectedChoreWeekDays.splice(index, 1);
                } else {
                    this.selectedChoreWeekDays.push(day);
                }
            },

            confirmChoreWeekDays() {
                if (this.disabled || !this.selectedChoreWeekDays.length) return;
                this.$emit('select-chore-weekdays', [...this.selectedChoreWeekDays]);
            },

            onChoreFormRoommateChange() {
                this.choreForm.roommateNames = this.choreForm.roommateIds.map(id => {
                    const selected = this.choreFormRoommateOptions.find(
                        item => String(item.value) === String(id)
                    );
                    return selected?.text || '';
                });
            },

            buildChoreSubmitPayload() {
                const pre = this.chorePre || {};
                const form = this.choreForm;
                this.onChoreFormRoommateChange();

                const weekDays = this.isWeeklyChoreFrequency
                    ? this.normalizeChoreWeekDays(form.weekDays)
                    : '';

                return {
                    ...pre,
                    choreTitle: String(form.choreTitle || '').trim(),
                    title: String(form.choreTitle || '').trim(),
                    frequency: form.frequency,
                    dueDate: form.dueDate || null,
                    startDate: form.dueDate || null,
                    weekDays,
                    WeekDays: weekDays,
                    notes: String(form.notes || '').trim(),
                    roommateId: form.roommateIds[0] || null,
                    roommateIds: [...form.roommateIds],
                    assignedUserId: form.roommateIds[0] || null,
                    assignedUserIds: [...form.roommateIds],
                    roommateName: form.roommateNames.join(', '),
                    roommateNames: [...form.roommateNames],
                    assignedUserName: form.roommateNames.join(', '),
                };
            },

            normalizeChoreWeekDays(value) {
                if (Array.isArray(value)) {
                    return value
                        .map(day => String(day || '').trim())
                        .filter(Boolean)
                        .join(',');
                }

                const text = String(value || '').trim();
                if (!text) return '';

                if (text.startsWith('[') && text.endsWith(']')) {
                    try {
                        const parsed = JSON.parse(text);
                        if (Array.isArray(parsed)) {
                            return parsed
                                .map(day => String(day || '').trim())
                                .filter(Boolean)
                                .join(',');
                        }
                    } catch (error) {
                        // Keep the original comma-separated value.
                    }
                }

                return text
                    .split(',')
                    .map(day => day.trim())
                    .filter(Boolean)
                    .join(',');
            },

            hasChoreWeekDays(chore) {
                return Boolean(this.normalizeChoreWeekDays(
                    chore?.weekDays || chore?.WeekDays || chore?.weekdays
                ));
            },

            formatChoreWeekDays(chore) {
                const weekDays = this.normalizeChoreWeekDays(
                    chore?.weekDays || chore?.WeekDays || chore?.weekdays
                );

                return weekDays
                    ? weekDays.split(',').join(', ')
                    : '';
            },

            onConfirmChore() {
                this.choreValidationTouched = true;

                const title = String(this.choreForm.choreTitle || '').trim();
                const frequency = String(this.choreForm.frequency || '').trim();
                const hasRoommates = this.choreForm.roommateIds.length > 0;
                const hasRequiredWeekDays =
                    !this.isWeeklyChoreFrequency ||
                    this.choreForm.weekDays.length > 0;

                // Mandatory fields match the web portal:
                // Chore Title, Frequency, Assign Roommate.
                if (!title || !frequency || !hasRoommates || !hasRequiredWeekDays) {
                    return;
                }

                const payload = this.buildChoreSubmitPayload();

                if (this.msg?.payload?.chore_data) {
                    this.msg.payload.chore_data.prefilled = {
                        ...(this.msg.payload.chore_data.prefilled || {}),
                        ...payload,
                    };
                }

                // Keep the existing parent confirm-chore flow unchanged.
                this.$emit('confirm-chore', this.msg);
            },

            choreKey(chore, index) {
                return String(chore?.id || chore?.Id || index);
            },

            toggleChoreDetails(chore, index) {
                const key = this.choreKey(chore, index);
                this.expandedChoreKey = this.expandedChoreKey === key ? '' : key;
            },

            isChoreDetailsOpen(chore, index) {
                return this.expandedChoreKey === this.choreKey(chore, index);
            },

            requestQuickChoreStatusChange(chore) {
                if (this.disabled || !chore) return;

                const currentStatus = String(chore.status || 'Pending').trim();

                this.choreStatusModal = {
                    show: true,
                    chore,
                    title: String(chore.title || 'Chore'),
                    currentStatus,
                    selectedStatus: currentStatus,
                };
            },

            closeQuickChoreStatusPopup() {
                this.choreStatusModal = {
                    show: false,
                    chore: null,
                    title: '',
                    currentStatus: '',
                    selectedStatus: '',
                };
            },

            confirmQuickChoreStatusChange() {
                if (this.disabled) return;

                const chore = this.choreStatusModal.chore;
                const newStatus = String(
                    this.choreStatusModal.selectedStatus || ''
                ).trim();

                const currentStatus = String(
                    this.choreStatusModal.currentStatus || ''
                ).trim();

                if (
                    !chore ||
                    !newStatus ||
                    newStatus.toLowerCase() === currentStatus.toLowerCase()
                ) {
                    return;
                }

                this.$emit('quick-chore-status-change', {
                    chore,
                    status: newStatus,
                    currentStatus,
                });

                this.closeQuickChoreStatusPopup();

                // Re-sync the row from MyChores after Genie finishes the update.
                setTimeout(() => this.loadMyChores(), 900);
                setTimeout(() => this.loadMyChores(), 2200);
            },

            requestEventGuestDelete(guest) {
                if (this.disabled || !guest) return;

                const guestId =
                    guest.guestId ||
                    guest.GuestId ||
                    guest.id ||
                    guest.Id ||
                    '';

                if (!guestId) return;

                this.eventGuestDeleteModal = {
                    show: true,
                    eventId: this.eventDetail?.eventId || '',
                    guestId: guestId,
                    guestName: String(
                        guest.fullName ||
                        guest.FullName ||
                        guest.name ||
                        'Guest'
                    ).trim(),
                };
            },

            closeEventGuestDeleteModal() {
                this.eventGuestDeleteModal = {
                    show: false,
                    eventId: '',
                    guestId: '',
                    guestName: '',
                };
            },

            confirmEventGuestDelete() {
                const data = {
                    eventId: this.eventGuestDeleteModal.eventId,
                    guestId: this.eventGuestDeleteModal.guestId,
                    guestName: this.eventGuestDeleteModal.guestName,
                };

                this.closeEventGuestDeleteModal();

                if (!data.eventId || !data.guestId) return;

                this.$emit('delete-event-guest', data);
            },

            requestChoreDelete(chore) {
                if (this.disabled || !chore) return;

                this.choreDeleteModal = {
                    show: true,
                    title: String(chore.title || chore.choreTitle || 'Chore').trim(),
                    chore: chore,
                };
            },

            closeChoreDeleteModal() {
                this.choreDeleteModal = {
                    show: false,
                    title: '',
                    chore: null,
                };
            },

            confirmChoreDelete() {
                const chore = this.choreDeleteModal.chore;

                this.closeChoreDeleteModal();

                if (!chore) return;

                // Preserve the existing parent delete flow exactly.
                this.$emit('delete-chore', chore);
            },

            getChoreRoommateDisplayName(roommate) {
                if (!roommate) {
                    return this.isBoatAsset ? 'Co-owner' : 'Roommate';
                }

                const rawName = String(
                    roommate.name ||
                    roommate.text ||
                    roommate.label ||
                    roommate.fullName ||
                    roommate.FullName ||
                    (this.isBoatAsset ? 'Co-owner' : 'Roommate')
                ).trim();

                // Strip any existing role suffix so we can re-label by asset type
                const baseName = rawName
                    .replace(/\s*\((primary\s+roommate|roommate|owner|co-owner|coowner)\)\s*$/i, '')
                    .trim() || rawName;

                const explicitPrimary =
                    roommate.isPrimaryRoommate === true ||
                    roommate.IsPrimaryRoommate === true ||
                    roommate.isPrimary === true ||
                    roommate.IsPrimary === true ||
                    roommate.primaryRoommate === true ||
                    roommate.PrimaryRoommate === true ||
                    roommate.isOwner === true ||
                    roommate.IsOwner === true;

                const roleText = String(
                    roommate.roommateType ||
                    roommate.RoommateType ||
                    roommate.role ||
                    roommate.Role ||
                    roommate.userType ||
                    roommate.UserType ||
                    roommate.assetRole ||
                    roommate.AssetRole ||
                    ''
                ).trim().toLowerCase();

                const roleSaysPrimary =
                    roleText === 'primary roommate' ||
                    roleText === 'primaryroommate' ||
                    roleText === 'primary' ||
                    roleText === 'owner' ||
                    roleText === 'asset owner';

                const roleSaysCoOwner =
                    roleText === 'co-owner' ||
                    roleText === 'coowner' ||
                    roleText === 'co owner';

                /*
                 * Fallback: logged-in user is treated as primary / owner.
                 */
                const loggedInUserId = String(
                    localStorage.getItem('userId') || ''
                ).trim().toLowerCase();

                const roommateUserId = String(
                    roommate.userId ||
                    roommate.UserId ||
                    roommate.id ||
                    roommate.Id ||
                    roommate.value ||
                    ''
                ).trim().toLowerCase();

                const isLoggedInPrimary =
                    !!loggedInUserId &&
                    !!roommateUserId &&
                    loggedInUserId === roommateUserId;

                const isPrimary =
                    explicitPrimary ||
                    roleSaysPrimary ||
                    isLoggedInPrimary;

                if (this.isBoatAsset) {
                    // Portal Boat labels: (Owner) / (Co-owner)
                    if (isPrimary) return `${baseName} (Owner)`;
                    if (roleSaysCoOwner) return `${baseName} (Co-owner)`;
                    return `${baseName} (Co-owner)`;
                }

                // Household: (Primary Roommate) / (Roommate)
                return `${baseName} (${isPrimary ? 'Primary Roommate' : 'Roommate'})`;
            },

            toggleAllChoreRoommates(event) {
                if (this.disabled) return;

                if (event?.target?.checked) {
                    this.selectedChoreRoommateIds =
                        this.choreRoommateOptions.map((roommate) =>
                            String(roommate.id || roommate.value)
                        );
                } else {
                    this.selectedChoreRoommateIds = [];
                }
            },

            formatGuestPhone(value) {
                const raw = String(value || '').trim();
                if (!raw) return '';

                // Preserve an international dialing prefix when supplied.
                const prefixMatch = raw.match(/^\s*(\+\d+(?:-\d+)?)\s*/);
                const prefix = prefixMatch ? prefixMatch[1] : '';
                const localPart = prefixMatch
                    ? raw.slice(prefixMatch[0].length)
                    : raw;

                const digits = localPart.replace(/\D/g, '');

                if (digits.length === 10) {
                    const formatted =
                        `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;

                    return prefix
                        ? `${prefix} ${formatted}`
                        : formatted;
                }

                return raw;
            },

            isPastEvent(event) {
                const rawDate = String(
                    event?.eventDate ||
                    event?.EventDate ||
                    ''
                ).trim();

                if (!rawDate) return false;

                let eventDate = null;

                // YYYY-MM-DD / ISO
                const iso = rawDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
                if (iso) {
                    eventDate = new Date(
                        Number(iso[1]),
                        Number(iso[2]) - 1,
                        Number(iso[3])
                    );
                }

                // MM-DD-YYYY / MM/DD/YYYY
                if (!eventDate) {
                    const us = rawDate.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
                    if (us) {
                        eventDate = new Date(
                            Number(us[3]),
                            Number(us[1]) - 1,
                            Number(us[2])
                        );
                    }
                }

                if (!eventDate || Number.isNaN(eventDate.getTime())) {
                    return false;
                }

                const today = new Date();
                today.setHours(0, 0, 0, 0);
                eventDate.setHours(0, 0, 0, 0);

                return eventDate < today;
            },

            formatEventPhonePrompt(text) {
                return String(text || '')
                    // bold markdown -> <strong>
                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                    // remove spaces/newlines before punctuation: "room party ." -> "room party."
                    .replace(/(<\/strong>|\w)\s+([.,!?;:])/g, '$1$2')
                    // collapse line breaks left in front of punctuation
                    .replace(/\s*\n\s*(?=[.,!?;:])/g, '')
                    .trim();
            },

            onEventGuestSourceChange() {
                if (!this.selectedEventGuestSource) return;

                this.$emit(
                    'select-event-guest-source',
                    this.selectedEventGuestSource
                );
            },

            toggleAllEventGuestRoommates(checked) {
                if (this.disabled) return;
                if (checked) {
                    this.selectedEventGuestRoommateIds =
                        this.eventGuestRoommateDropdownOptions.map(r => String(r.value));
                } else {
                    this.selectedEventGuestRoommateIds = [];
                }
            },
            confirmEventGuestRoommateSelection() {
                if (this.disabled || !this.selectedEventGuestRoommateIds.length) return;

                const options = this.eventGuestRoommateDropdownOptions;
                const selected = options.filter(r =>
                    this.selectedEventGuestRoommateIds.includes(String(r.value))
                );
                if (!selected.length) return;

                const ids = selected.map(r => String(r.value));
                const names = selected.map(r => r.text);
                const first = selected[0].original || {};

                this.$emit('select-event-guest-roommate', {
                    ...first,
                    id: ids.join(','),          // one person: unchanged. Several: all IDs
                    value: ids[0],
                    ids,
                    names,
                    name: names.join(', '),
                    text: names.join(', '),
                    fullName: names.join(', '),
                    isAll: options.length > 1 && selected.length === options.length,
                    selectedRoommates: selected.map(r => r.original),
                });
            },

            async GetCountryCodeList() {
                try {
                    const response = await UserService.countryList(
                        'countryCode',
                        '00Test',
                        localStorage.getItem('companyId')
                    );

                    this.countryCodeList = response?.data || [];

                    const us = findDefaultUsCountryCode(this.countryCodeList)
                        || this.countryCodeList.find((item) =>
                            String(item.text || '').includes('US')
                        );

                    if (us) {
                        this.eventGuestPhone.countryCode = us.value;
                        this.eventGuestPhoneLength =
                            resolvePhoneLength(us.value, this.countryCodeList) || 10;
                    }
                } catch (e) {
                    console.error('Event guest country code load failed', e);
                }
            },

            onEventGuestCountryCodeChange() {
                this.eventGuestPhoneLength =
                    resolvePhoneLength(
                        this.eventGuestPhone.countryCode,
                        this.countryCodeList
                    ) || 10;

                this.eventGuestPhone.phoneNumber = '';
                this.eventGuestPhoneError = '';

                this.$emit('event-guest-phone-validity-change', false);
            },

            onEventGuestPhoneInput(event) {
                const maxLength = this.eventGuestPhoneLength || 10;

                const digits = String(event?.target?.value || '')
                    .replace(/\D/g, '')
                    .slice(0, maxLength);

                let formatted = digits;

                if (maxLength === 10) {
                    if (digits.length > 6) {
                        formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
                    } else if (digits.length > 3) {
                        formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
                    } else if (digits.length > 0) {
                        formatted = `(${digits}`;
                    }
                }

                this.eventGuestPhone.phoneNumber = formatted;
                this.eventGuestPhoneError = '';

                this.$emit(
                    'event-guest-phone-validity-change',
                    this.isEventGuestPhoneComplete
                );
            },

            resolveEventGuestDialCode() {
                const value = this.eventGuestPhone.countryCode;
                if (!value) return '';

                const selected = (this.countryCodeList || [])
                    .find((item) => item.value === value);

                if (!selected || !selected.text) return value;

                const dialCode = String(selected.text).trim().split(/\s+/)[0];
                return dialCode || value;
            },

            submitEventGuestPhone() {
                this.eventGuestPhoneError = '';

                if (!this.eventGuestPhone.countryCode) {
                    this.eventGuestPhoneError = 'Please select a country code.';
                    return;
                }

                const digits = String(this.eventGuestPhone.phoneNumber || '')
                    .replace(/\D/g, '');

                const requiredLength = this.eventGuestPhoneLength || 10;

                if (digits.length !== requiredLength) {
                    this.eventGuestPhoneError =
                        `Enter a valid ${requiredLength}-digit phone number.`;
                    return;
                }

                const dialCode = this.resolveEventGuestDialCode();

                let formattedPhone = digits;

                if (requiredLength === 10) {
                    formattedPhone =
                        `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
                }

                const fullPhoneNumber = dialCode
                    ? `${dialCode} ${formattedPhone}`
                    : formattedPhone;

                this.$emit('submit-event-guest-phone', fullPhoneNumber);
            },

            formatTimeNoSeconds(value) {
                if (!value) return '—';

                const parts = String(value).trim().split(':');
                if (parts.length < 2) return String(value);

                return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
            },

            formatTime(value) {
                if (!value) return '—';

                const parts = String(value).split(':');
                if (parts.length < 2) return value;

                let hours = parseInt(parts[0], 10);
                const minutes = parts[1];

                const period = hours >= 12 ? 'PM' : 'AM';
                hours = hours % 12 || 12;

                return `${String(hours).padStart(2, '0')} : ${minutes} ${period}`;
            },
            pillStyle(color) {
                return {
                    background: `${color}1a`,
                    color,
                    border: `1px solid ${color}55`,
                };
            },
            async loadEventAddressLikePortal() {
                // Same data sources as Event/addedit.vue create flow
                await this.ensureAddressLists();
                await this.loadUserAddressFromProfile();
            },
            async ensureAddressLists() {
                if (this.countryList.length && this.stateList.length) return;
                try {
                    const [countries, states] = await Promise.all([
                        UserService.countryList('country', '00Test', localStorage.getItem('userId')),
                        UserService.getStateList(),
                    ]);
                    this.countryList = countries?.data || [];
                    this.stateList = states?.data || [];
                } catch (e) {
                    console.error('ChatbotChoresEvents address lists', e);
                }
            },
            /**
             * Prefill address from logged-in user profile — same as portal Create Event (GetUserData).
             * Do NOT use AI/chatbot addressLine1.
             */
            async loadUserAddressFromProfile() {
                const userId = localStorage.getItem('userId');
                if (!userId) return;
                // Avoid re-fetching for the same preview card
                if (this._addressLoadedKey === this._formInitKey) return;
                try {
                    const response = await UserService.getUserDetail(userId, 'optionalTest');
                    const result = response?.data;
                    if (!result) return;

                    let stateName = null;
                    if (result.county != US_COUNTRY_ID) {
                        stateName = result.stateName;
                    }

                    this.eventForm.addressLine1 = result.address ? result.address : '';
                    this.eventForm.addressLine2 = result.address2 ? result.address2 : '';
                    this.eventForm.city = result.city ? result.city : '';
                    this.eventForm.zip = result.zipCode ? result.zipCode : '';
                    this.eventForm.country = result.county ? result.county : US_COUNTRY_ID;
                    this.eventForm.state = stateName == null ? (result.state || null) : null;
                    this.eventForm.stateName = stateName || '';
                    this._addressLoadedKey = this._formInitKey;
                } catch (e) {
                    console.error('ChatbotChoresEvents GetUserData', e);
                }
            },
            initEventFormFromPayload() {
                const pre = this.eventPre || {};
                const data = this.eventData || {};
                // Re-init only when a new preview card arrives (ignore AI address)
                const feeKey =
                    pre.entryFee ?? pre.fee ?? pre.amount ??
                    data.entryFee ?? data.fee ?? data.amount ??
                    data.prefilled?.entryFee ?? data.prefilled?.fee ?? data.prefilled?.amount ??
                    '';
                const key = `${pre.title || data.title || ''}|${pre.eventDate || data.eventDate || ''}|${pre.startTime || data.startTime || ''}|${feeKey}`;
                if (key === this._formInitKey && this.eventForm.title) return;
                this._formInitKey = key;
                this._addressLoadedKey = '';

                const rawEventDate = String(pre.eventDate || data.eventDate || '');
                const normalizedEventDate = rawEventDate
                    ? rawEventDate.slice(0, 10)
                    : '';

                const rawStartTime = String(pre.startTime || data.startTime || '');
                const normalizedStartTime = rawStartTime
                    ? rawStartTime.slice(0, 5)
                    : '';

                // Keep the amount already collected by the chatbot.
                // Backend payloads can expose it as entryFee / fee / amount (prefilled or top-level).
                const rawEntryFee =
                    pre.entryFee ??
                    pre.fee ??
                    pre.amount ??
                    data.entryFee ??
                    data.fee ??
                    data.amount ??
                    data.prefilled?.entryFee ??
                    data.prefilled?.fee ??
                    data.prefilled?.amount ??
                    0;

                const parsedEntryFee = Number(
                    String(rawEntryFee || 0).replace(/[$,]/g, '')
                ) || 0;

                const backendAccessType = String(
                    pre.accessType ||
                    pre.access_type ||
                    data.accessType ||
                    data.access_type ||
                    data.prefilled?.accessType ||
                    ''
                ).toLowerCase();

                const isPaidEvent =
                    backendAccessType === 'paid' ||
                    parsedEntryFee > 0;

                this.eventValidationTouched = false;

                this.eventForm = {
                    ...emptyEventForm(),
                    title: pre.title || data.title || '',
                    accessType: isPaidEvent ? 'paid' : 'unpaid',
                    entryFee: isPaidEvent ? parsedEntryFee : 0,
                    eventDate: normalizedEventDate,
                    startTime: normalizedStartTime,
                    // Address comes from user profile API (portal Create Event), not AI
                    addressLine1: '',
                    addressLine2: '',
                    country: US_COUNTRY_ID,
                    state: null,
                    stateName: '',
                    city: '',
                    zip: '',
                    message: pre.message || '',
                    companyId: pre.companyId || null,
                    actorUserId: pre.actorUserId || localStorage.getItem('userId'),
                };
            },
            onCountryChange() {
                this.eventForm.state = null;
                this.eventForm.stateName = '';
            },

            onEventAccessTypeChange() {
                // Same behavior as the existing event reference:
                // Unpaid events are submitted with entryFee = 0.
                if (this.eventForm.accessType === 'unpaid') {
                    this.eventForm.entryFee = 0;
                }
            },

            buildEventSubmitPayload() {
                const f = this.eventForm;
                const state =
                    f.country === US_COUNTRY_ID
                        ? (f.state || null)
                        : ((f.stateName || '').trim() || null);

                return {
                    ...this.eventPre,
                    title: String(f.title || '').trim(),
                    entryFee:
                        f.accessType === 'paid'
                            ? (Number(f.entryFee) || 0)
                            : 0,
                    eventDate: f.eventDate,
                    startTime:
                        f.startTime
                            ? (String(f.startTime).length === 5
                                ? `${f.startTime}:00`
                                : f.startTime)
                            : '',
                    addressLine1: (f.addressLine1 || '').trim() || null,
                    addressLine2: (f.addressLine2 || '').trim() || null,
                    country: f.country || null,
                    city: (f.city || '').trim() || null,
                    state,
                    stateName: f.country === US_COUNTRY_ID ? null : ((f.stateName || '').trim() || null),
                    zip: (f.zip || '').trim() || null,
                    message: f.message || '',
                    companyId: f.companyId,
                    actorUserId: f.actorUserId,
                };
            },
            onConfirmEvent() {
                this.eventValidationTouched = true;

                const title = String(this.eventForm.title || '').trim();
                const eventDate = String(this.eventForm.eventDate || '').trim();

                // Event Title and Date are compulsory, matching the portal form.
                if (!title || !eventDate) {
                    return;
                }

                const payload = this.buildEventSubmitPayload();

                if (this.msg?.payload?.event_data) {
                    this.msg.payload.event_data.prefilled = {
                        ...(this.msg.payload.event_data.prefilled || {}),
                        ...payload,
                    };
                }

                this.$emit('confirm-event', this.msg);
            },
        },
    };</script>

<style scoped>
    .ce-card {
        background: #fff;
        border-radius: 16px;
        box-shadow: 0 4px 18px rgba(0, 0, 0, 0.12);
        overflow: hidden;
        font-size: 13px;
        min-width: 280px;
        max-width: 360px;
    }

    .ce-card--form {
        max-width: 380px;
    }

    .ce-card__header {
        /* Match chatbot ticket-preview-header blue */
        background: linear-gradient(135deg, rgb(15, 102, 184), rgb(18, 35, 65));
        color: #fff;
        padding: 14px 18px;
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .ce-card__icon {
        font-size: 22px;
    }

    .ce-card__title {
        font-weight: 700;
        font-size: 14px;
    }

    .ce-card__sub {
        font-size: 11px;
        opacity: 0.8;
    }

    .ce-card__body {
        padding: 14px 18px;
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    /* Chore Details - editable form matching web portal */
    .ce-assignees {
        border: 1px solid #cbd5e1;
        border-radius: 5px;
        background: #fff;
        color: #475569;
        font-size: 13px;
    }

        .ce-assignees > summary {
            padding: 9px;
            cursor: pointer;
            overflow-wrap: anywhere;
        }

    .ce-assignees-panel {
        max-height: 230px;
        overflow-y: auto;
        border-top: 1px solid #e2e8f0;
    }

        .ce-assignees-panel > label {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 9px 10px;
            margin: 0;
            cursor: pointer;
        }

        .ce-assignees-panel input {
            flex: 0 0 auto;
            accent-color: #17345c;
        }

    .ce-assignees-all {
        font-weight: 600;
        border-bottom: 1px solid #e2e8f0;
    }

    .ce-chore-form-field {
        display: flex;
        flex-direction: column;
        gap: 5px;
    }

    .ce-chore-form-field--last {
        margin-bottom: 0;
    }

    .ce-chore-form-label {
        color: #64748b;
        font-size: 11px;
        font-weight: 600;
    }

    .ce-chore-validation-error {
        color: #dc2626;
        font-size: 10px;
        font-weight: 600;
        line-height: 1.25;
    }

    .ce-required {
        color: #dc2626 !important;
        font-weight: 700 !important;
    }

    .ce-chore-form-field textarea.ce-input {
        resize: vertical;
        min-height: 62px;
    }

    .ce-row {
        display: flex;
        justify-content: space-between;
        border-bottom: 1px solid #f0f0f0;
        padding-bottom: 8px;
        gap: 12px;
    }

    .ce-row--last {
        border-bottom: none;
        padding-bottom: 0;
    }

    .ce-label {
        color: #888;
        font-size: 11px;
        font-weight: 600;
        flex-shrink: 0;
    }

    .ce-value {
        color: #1e2d45;
        font-weight: 600;
        font-size: 12px;
        text-align: right;
    }

    .ce-value--wrap {
        max-width: 200px;
    }

    /* Event Details - portal-style editable fields */
    .ce-event-form-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding-bottom: 8px;
        border-bottom: 1px solid #f0f0f0;
    }

    .ce-event-form-label {
        flex: 0 0 auto;
        color: #888;
        font-size: 11px;
        font-weight: 600;
    }

    .ce-required {
        color: #dc2626 !important;
        font-weight: 700;
    }

    .ce-event-top-input {
        width: 170px;
        max-width: 170px;
        min-height: 34px;
        text-align: right;
        flex: 0 0 170px;
    }

    .ce-access-type {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 12px;
    }

    .ce-radio-label {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        margin: 0;
        color: #1e2d45;
        font-size: 12px;
        font-weight: 500;
        cursor: pointer;
    }

        .ce-radio-label input[type='radio'] {
            margin: 0;
            cursor: pointer;
        }

            .ce-radio-label input[type='radio']:disabled {
                cursor: not-allowed;
            }

    .ce-event-validation-error {
        margin-top: -6px;
        color: #dc2626;
        font-size: 10px;
        font-weight: 600;
        text-align: right;
    }

    @media (max-width: 420px) {
        .ce-event-top-input {
            width: 155px;
            max-width: 155px;
            flex-basis: 155px;
        }

        .ce-event-form-row {
            gap: 8px;
        }

        .ce-access-type {
            gap: 8px;
        }
    }

    .ce-addr-title {
        font-size: 12px;
        font-weight: 700;
        color: #1e2d45;
        margin-top: 4px;
        padding-top: 4px;
        border-top: 1px dashed #e5e7eb;
    }

    .ce-field {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin-bottom: 4px; /* extra space between fields */
    }

    .ce-field--last {
        margin-bottom: 0;
    }

    .ce-field label {
        color: #888;
        font-size: 11px;
        font-weight: 600;
    }

    .ce-input {
        width: 100%;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        padding: 8px 10px;
        font-size: 12px;
        color: #1e2d45;
        background: #fff;
        resize: vertical;
        box-sizing: border-box;
    }

        .ce-input:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }

    .ce-dropdown {
        width: 100%;
        font-size: 12px;
    }

    .ce-card__actions {
        padding: 0 18px 16px;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .ce-card__pad {
        padding: 0 16px 12px;
    }

    :deep(.ce-dropdown.p-dropdown) {
        width: 100%;
        min-height: 40px;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        background: #fff;
    }

    :deep(.ce-dropdown .p-dropdown-label) {
        padding: 9px 10px;
        font-size: 12px;
        color: #1e2d45;
        /* leave space for clear + chevron */
        padding-right: 4px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    :deep(.ce-dropdown .p-dropdown-clear-icon) {
        color: #dc2626;
        font-size: 12px;
        margin-right: 2px;
    }

    :deep(.ce-dropdown .p-dropdown-trigger) {
        width: 28px;
        min-width: 28px;
    }

    :deep(.ce-dropdown .p-dropdown-trigger-icon) {
        font-size: 12px;
    }

    .ce-btn {
        border: none;
        border-radius: 10px;
        padding: 10px;
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
    }

        .ce-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }

    .ce-btn--primary {
        background: linear-gradient(135deg, rgb(15, 102, 184), rgb(18, 35, 65));
        color: #fff;
    }

    .ce-btn--secondary {
        width: 100%;
        background: #f1f5f9;
        color: #1e2d45;
        border: 1.5px solid #cbd5e1;
        font-weight: 600;
    }

    .ce-btn--link {
        background: none;
        color: #aaa;
        font-size: 12px;
        font-weight: 500;
        text-decoration: underline;
        padding: 4px;
    }

    .ce-btn--danger-link,
    .ce-btn--accent-link,
    .ce-btn--success-link,
    .ce-btn--warn-link {
        background: none;
        border: none;
        font-size: 11px;
        font-weight: 600;
        cursor: pointer;
        padding: 2px 0;
        text-align: left;
    }

    .ce-btn--danger-link {
        color: #dc2626;
    }

    .ce-btn--accent-link {
        color: rgb(15, 102, 184);
    }

    .ce-btn--success-link {
        color: #16a34a;
    }

    .ce-btn--warn-link {
        color: #d97706;
    }

    .ce-list-body {
        padding: 0 16px 8px !important;
    }

    /*.ce-list-item {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 8px;
        padding: 10px 0;
        border-bottom: 1px solid var(--clr-border, #e5e7eb);
    }*/

    .ce-list-item {
        display: grid;
        grid-template-columns: 3fr 1fr;
        padding: 10px 0;
        border-bottom: 1px solid var(--clr-border, #e5e7eb);
    }

    .ce-chore-details {
        grid-column: 1 / -1;
    }

    .ce-list-item--last {
        border-bottom: none;
    }

    .ce-list-item__main {
        flex: 1;
        min-width: 0;
    }

    .ce-list-item__title {
        font-weight: 600;
        font-size: 13px;
    }

    .ce-list-item__meta {
        font-size: 11px;
        color: var(--clr-text-muted, #888);
        margin-top: 3px;
    }

    .ce-list-item__tags {
        margin-top: 4px;
        display: flex;
        gap: 6px;
        align-items: center;
        flex-wrap: wrap;
    }

    .ce-list-item__due {
        font-size: 11px;
        color: var(--clr-text-muted, #888);
    }

    .ce-list-item__actions {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 3px;
        flex-shrink: 0;
    }

    /* =========================================================
       CHORE DETAILS BOX
       Restyled to match the Roommate details card design:
       roomier padding, rounded corners, label/value rows with
       larger, more legible type and right-aligned wrapping values.
       ========================================================= */
    .ce-chore-details {
        margin-top: 10px;
        padding: 12px 14px;
        border: 1px solid #dbe3ec;
        border-radius: 10px;
        background: #f8fafc;
        box-sizing: border-box;
    }

    .ce-chore-detail-row {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 14px;
        padding: 9px 0;
        border-bottom: 1px solid #e5e7eb;
        font-size: 12px;
        line-height: 1.5;
    }

        .ce-chore-detail-row:last-child {
            border-bottom: 0;
            padding-bottom: 2px;
        }

        .ce-chore-detail-row > span {
            flex: 0 0 auto;
            min-width: 92px;
            color: #64748b;
            font-size: 12px;
            font-weight: 600;
        }

        .ce-chore-detail-row > strong {
            flex: 1;
            min-width: 0;
            color: #1e2d45;
            font-size: 12px;
            font-weight: 600;
            text-align: right;
            overflow-wrap: anywhere;
            line-height: 1.5;
        }

    .ce-chore-status-clickable {
        border-style: solid;
        cursor: pointer;
        font-family: inherit;
        line-height: 1.2;
    }

        .ce-chore-status-clickable:hover:not(:disabled) {
            filter: brightness(0.97);
        }

        .ce-chore-status-clickable:disabled {
            cursor: not-allowed;
            opacity: .55;
        }

    /* Chore Status popup - same CSS pattern as other module confirmation popups */
    .ce-chore-status-popup-overlay {
        position: fixed;
        inset: 0;
        z-index: 999999;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 16px;
        box-sizing: border-box;
        background: rgba(0, 0, 0, 0.48);
    }

    .ce-chore-status-popup-card {
        width: min(420px, calc(100vw - 32px));
        max-width: 420px;
        background: #ffffff;
        border-radius: 4px;
        overflow: hidden;
        box-shadow: 0 4px 18px rgba(0, 0, 0, 0.28);
        text-align: left;
    }

    .ce-chore-status-popup-header {
        min-height: 40px;
        display: flex;
        align-items: center;
        padding: 9px 13px;
        box-sizing: border-box;
        background: #132a4e;
        color: #ffffff;
        font-size: 14px;
        font-weight: 600;
        line-height: 1.3;
    }

    .ce-chore-status-popup-body {
        padding: 16px 16px 12px;
        background: #ffffff;
    }

    .ce-chore-status-popup-title {
        margin: 0 0 12px;
        color: #222222;
        font-size: 13px;
        font-weight: 600;
        line-height: 1.5;
        overflow-wrap: anywhere;
    }

    .ce-chore-status-popup-field {
        margin-bottom: 18px;
    }

    .ce-chore-status-popup-label {
        display: block;
        margin-bottom: 6px;
        color: #222222;
        font-size: 12px;
        font-weight: 500;
        line-height: 1.3;
    }

    .ce-chore-status-required {
        color: #dc2626;
    }

    .ce-chore-status-popup-dropdown {
        width: 100%;
        min-height: 36px;
        font-size: 12px;
    }

        .ce-chore-status-popup-dropdown.p-dropdown {
            border: 1px solid #cbd5e1;
            border-radius: 4px;
            box-shadow: none;
        }

            .ce-chore-status-popup-dropdown.p-dropdown:not(.p-disabled):hover {
                border-color: #93c5fd;
            }

            .ce-chore-status-popup-dropdown.p-dropdown:not(.p-disabled).p-focus {
                border-color: #60a5fa;
                box-shadow: 0 0 0 1px #60a5fa;
            }

        .ce-chore-status-popup-dropdown .p-dropdown-label {
            padding: 8px 10px;
            font-size: 12px;
            color: #475569;
        }

        .ce-chore-status-popup-dropdown .p-dropdown-trigger {
            width: 34px;
        }

    .ce-chore-status-popup-actions {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 8px;
        margin-top: 0;
    }

    .ce-chore-status-popup-btn {
        min-width: 76px;
        height: 32px;
        padding: 5px 14px;
        border: 0;
        border-radius: 3px;
        font-family: inherit;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
    }

    .ce-chore-status-popup-ok {
        background: #132a4e;
        color: #ffffff;
    }

        .ce-chore-status-popup-ok:hover:not(:disabled) {
            background: #0f223f;
        }

    .ce-chore-status-popup-cancel {
        background: #cc3b30;
        color: #ffffff;
    }

        .ce-chore-status-popup-cancel:hover {
            background: #b83228;
        }

    .ce-chore-status-popup-btn:disabled {
        background: #9ca3af;
        color: #ffffff;
        opacity: 1;
        cursor: not-allowed;
    }

    @media (max-width: 767px) {

        /* Chore details on mobile: "Label : value" on one line */
        .ce-chore-detail-row {
            flex-direction: row;
            align-items: flex-start;
            justify-content: flex-start;
            gap: 6px;
        }

            .ce-chore-detail-row > span {
                flex: 0 0 auto;
                min-width: 104px;
            }

                .ce-chore-detail-row > span::after {
                    content: ' :';
                }

            .ce-chore-detail-row > strong {
                flex: 1;
                min-width: 0;
                text-align: left;
            }

        .ce-event-detail-card .ce-guest-row-actions {
            gap: 0px !important;
            padding: 0px !important;
            background: none !important;
            border: none !important;
        }

        .ce-guest-row {
            grid-template-columns: minmax(0, 1fr) 32px !important;
        }
    }

    @media (max-width: 480px) {
        .ce-chore-status-popup-overlay {
            padding: 12px;
        }

        .ce-chore-status-popup-card {
            width: 100%;
            max-width: 340px;
        }

        .ce-chore-status-popup-header {
            font-size: 13px;
        }

        .ce-chore-status-popup-title {
            font-size: 12px;
        }
    }

    .ce-pill {
        padding: 1px 8px;
        border-radius: 10px;
        font-size: 10px;
        font-weight: 600;
    }

    .ce-empty {
        text-align: center;
        color: #999;
        font-size: 12px;
        padding: 16px 0;
    }

    .ce-guest-heading {
        margin-top: 8px;
        font-size: 11px;
        font-weight: 700;
        color: #888;
    }

    .ce-guest-row {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        align-items: start;
        gap: 10px;
        padding: 10px 0;
        border-bottom: 1px dashed #e5e7eb;
    }

        .ce-guest-row > div:first-child {
            min-width: 0;
        }

        .ce-guest-row .ce-list-item__tags {
            margin-top: 0;
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 5px;
            flex-wrap: nowrap;
        }

    .ce-guest-name {
        margin-bottom: 3px;
        color: #1e2d45;
        font-weight: 700;
        font-size: 12px;
        line-height: 1.3;
    }

    .ce-guest-contact {
        max-width: 100%;
        color: var(--clr-text-muted, #7b8794);
        font-size: 10px;
        line-height: 1.45;
        overflow-wrap: anywhere;
        word-break: normal;
    }

    .ce-guest-row .ce-pill {
        min-width: 64px;
        padding: 2px 8px;
        text-align: center;
        box-sizing: border-box;
        white-space: nowrap;
    }

    /* Chore status update - same form/card style as other modules */
    .ce-chore-status-card {
        width: 100%;
        min-width: 280px;
        max-width: 360px;
        overflow: hidden;
        background: #ffffff;
        border-radius: 16px;
        box-shadow: 0 4px 18px rgba(0, 0, 0, 0.12);
        box-sizing: border-box;
    }

    .ce-chore-status-body {
        padding: 12px !important;
        gap: 8px !important;
    }

    .ce-chore-status-option {
        width: 100%;
        min-height: 44px;
        padding: 10px 12px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        border: 1px solid #d7dce2;
        border-radius: 10px;
        background: #ffffff;
        color: #1e2d45;
        font-family: inherit;
        font-size: 12px;
        font-weight: 600;
        line-height: 1.4;
        text-align: left;
        cursor: pointer;
        box-sizing: border-box;
        transition: background .15s ease, border-color .15s ease, box-shadow .15s ease, transform .05s ease;
    }

        .ce-chore-status-option:hover:not(:disabled) {
            background: #f0f7ff;
            border-color: #0f66b8;
            box-shadow: 0 0 0 2px rgba(15, 102, 184, .08);
        }

        .ce-chore-status-option:active:not(:disabled) {
            transform: translateY(1px);
        }

        .ce-chore-status-option:disabled {
            opacity: .5;
            cursor: not-allowed;
        }

    .ce-chore-status-option__text {
        flex: 1;
        min-width: 0;
        color: #1e2d45;
        overflow-wrap: anywhere;
    }

    .ce-chore-status-option__arrow {
        flex: 0 0 auto;
        color: #0f66b8;
        font-size: 16px;
        font-weight: 700;
        line-height: 1;
    }

    /* Chore Status Update - blue clickable form only */
    .ce-status-flow-card {
        width: 100%;
        min-width: 280px;
        max-width: 360px;
        background: #ffffff;
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 4px 18px rgba(15, 35, 65, 0.14);
        box-sizing: border-box;
    }

    .ce-weekdays-card {
        width: 100%;
        min-width: 280px;
        max-width: 330px;
        overflow: hidden;
        border-radius: 14px;
        background: #fff;
        box-shadow: 0 4px 16px rgba(15, 35, 65, 0.14);
        box-sizing: border-box;
    }

    .ce-weekdays-header {
        padding: 14px 16px;
        border-bottom: 2px solid #f59e0b;
        background: linear-gradient(135deg, #122341, #172b4d);
        color: #fff;
        font-size: 13px;
        font-weight: 600;
        line-height: 1.4;
    }

    .ce-weekdays-options {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 4px;
        width: 100%;
        padding: 12px 10px;
        box-sizing: border-box;
    }

    .ce-weekday-btn {
        flex: 1 1 0;
        min-width: 0;
        width: auto;
        max-width: 32px;
        height: auto;
        aspect-ratio: 1 / 1;
        padding: 0;
        border: 1.5px solid #cbd5e1;
        border-radius: 50%;
        background: #fff;
        color: #1e2d45;
        font-family: inherit;
        font-size: 12px;
        font-weight: 700;
        cursor: pointer;
        box-sizing: border-box;
    }


    .ce-weekday-btn--selected {
        border-color: #0f66b8;
        background: #0f66b8;
        color: #fff;
    }

    .ce-weekday-btn:disabled,
    .ce-weekdays-confirm:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }

    .ce-weekdays-confirm {
        display: block;
        width: 120px;
        min-height: 36px;
        margin: 0 auto 12px;
        border: 0;
        border-radius: 8px;
        background: #0f66b8;
        color: #fff;
        font-family: inherit;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        box-sizing: border-box;
    }

    .ce-status-flow-header {
        padding: 13px 16px;
        background: linear-gradient(135deg, rgb(15, 102, 184), rgb(18, 35, 65));
        color: #ffffff;
        font-size: 13px;
        font-weight: 700;
        line-height: 1.4;
    }

    .ce-status-flow-options {
        padding: 12px;
        display: flex;
        flex-direction: column;
        gap: 8px;
        background: #ffffff;
    }

    .ce-status-flow-option {
        width: 100%;
        min-height: 44px;
        padding: 10px 12px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        border: 1px solid #d7dce2;
        border-radius: 9px;
        background: #ffffff;
        color: #1e2d45;
        font-family: inherit;
        font-size: 12px;
        font-weight: 600;
        line-height: 1.4;
        text-align: left;
        cursor: pointer;
        box-sizing: border-box;
    }

        .ce-status-flow-option:hover:not(:disabled) {
            border-color: #0f66b8;
            background: #f2f8ff;
        }

        .ce-status-flow-option:disabled {
            opacity: .5;
            cursor: not-allowed;
        }

    .ce-status-flow-arrow {
        flex: 0 0 auto;
        color: #0f66b8;
        font-size: 16px;
        font-weight: 700;
    }

    /* Event Guest Source - reference style */
    .ce-event-guest-source-card {
        width: 100%;
        min-width: 290px;
        max-width: 370px;
        overflow: hidden;
        background: #ffffff;
        border: 1px solid #e6e9ef;
        border-radius: 14px;
        box-shadow: 0 6px 20px rgba(15, 35, 65, .14);
        box-sizing: border-box;
    }

    .ce-event-guest-source-header {
        padding: 12px 14px;
        background: linear-gradient(135deg, #163f73 0%, #102a4d 100%);
        color: #ffffff;
        font-size: 12px;
        font-weight: 700;
        line-height: 1.45;
        border-bottom: 2px solid #f3a51f;
    }

    .ce-event-guest-source-options {
        padding: 9px 10px 10px;
        display: flex;
        flex-direction: column;
        gap: 7px;
        background: #ffffff;
    }

    .ce-event-guest-source-option {
        width: 100%;
        min-height: 40px;
        padding: 9px 12px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        border: 1px solid #dedfe3;
        border-radius: 9px;
        background: #f8f8f8;
        color: #2d3748;
        font-family: inherit;
        font-size: 12px;
        font-weight: 600;
        text-align: left;
        cursor: pointer;
        box-sizing: border-box;
        transition: background .15s ease, border-color .15s ease;
    }

        .ce-event-guest-source-option:hover:not(:disabled) {
            background: #eef6ff;
            border-color: #0f66b8;
        }

        .ce-event-guest-source-option:disabled {
            opacity: .5;
            cursor: not-allowed;
        }

    .ce-event-guest-source-option__text {
        flex: 1;
        min-width: 0;
    }

    .ce-event-guest-source-option__arrow {
        flex: 0 0 auto;
        color: #a8b0bb;
        font-size: 14px;
        font-weight: 700;
    }

    /* Event Guest Source / Roommate Picker */
    .ce-event-guest-card {
        width: 100%;
        min-width: 290px;
        max-width: 370px;
        overflow: hidden;
        background: #ffffff;
        border: 1px solid rgba(203, 213, 225, .85);
        border-radius: 14px;
        box-shadow: 0 6px 20px rgba(15, 35, 65, .16);
        box-sizing: border-box;
    }

    .ce-event-guest-header {
        width: 100%;
        padding: 13px 16px;
        background: linear-gradient( 135deg, rgb(15, 102, 184), rgb(18, 35, 65) );
        color: #ffffff;
        font-size: 13px;
        font-weight: 700;
        line-height: 1.45;
        box-sizing: border-box;
    }

    .ce-event-guest-form {
        width: 100%;
        padding: 14px;
        background: #ffffff;
        box-sizing: border-box;
    }

    .ce-event-guest-label {
        display: block;
        margin-bottom: 6px;
        color: #64748b;
        font-size: 11px;
        font-weight: 700;
    }

    .ce-event-guest-dropdown {
        width: 100%;
    }

    :deep(.ce-event-guest-dropdown.p-dropdown) {
        width: 100% !important;
        min-height: 42px;
        border: 1px solid #cbd5e1;
        border-radius: 8px;
        background: #ffffff;
        box-shadow: none;
    }

    :deep(.ce-event-guest-dropdown .p-dropdown-label) {
        padding: 10px 12px;
        color: #1e2d45;
        font-size: 12px;
    }

    :deep(.ce-event-guest-dropdown .p-dropdown-trigger) {
        width: 38px;
        color: #64748b;
    }

    :deep(.ce-event-guest-dropdown .p-dropdown-panel) {
        z-index: 99999 !important;
    }

    :deep(.ce-event-guest-dropdown .p-dropdown-item) {
        padding: 10px 12px;
        font-size: 12px;
        color: #1e2d45;
    }

    .ce-event-guest-empty {
        padding: 12px 4px 2px;
        color: #8a94a3;
        font-size: 12px;
        text-align: center;
    }

    :deep(.p-dropdown-panel) {
        z-index: 999999 !important;
    }

    :deep(.p-dropdown-panel .p-dropdown-items-wrapper) {
        max-height: 220px !important;
        overflow-y: auto !important;
    }

    :deep(.p-dropdown-panel .p-dropdown-items) {
        padding: 4px 0 !important;
    }

    :deep(.p-dropdown-panel .p-dropdown-item) {
        padding: 10px 12px !important;
        font-size: 12px !important;
        cursor: pointer !important;
        color: #1e2d45 !important;
    }

    /* Chore roommate selector */
    .ce-roommate-select-all,
    .ce-roommate-option {
        display: flex;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        color: #1e2d45;
        font-size: 13px;
        line-height: 1.4;
    }

    .ce-roommate-select-all {
        margin-bottom: 0;
        font-weight: 700;
    }

    .ce-roommate-option {
        margin-bottom: 10px;
        font-weight: 400;
    }

        .ce-roommate-option:last-child {
            margin-bottom: 0;
        }

        .ce-roommate-select-all input,
        .ce-roommate-option input {
            flex: 0 0 auto;
            margin: 0;
            cursor: pointer;
        }

            .ce-roommate-select-all input:disabled,
            .ce-roommate-option input:disabled {
                cursor: not-allowed;
            }

    .ce-roommate-divider {
        width: 100%;
        height: 1px;
        margin: 2px 0 2px;
        background: #edf0f3;
    }

    .ce-phone-card {
        width: 100%;
        min-width: 280px;
        max-width: 360px;
        padding: 16px;
        background: #ffffff;
        border-radius: 16px;
        box-shadow: 0 4px 18px rgba(0, 0, 0, 0.12);
        box-sizing: border-box;
        font-size: 13px;
    }

    .ce-phone-question {
        margin: -14px -14px 12px -14px;
        padding: 12px 14px;
        background: linear-gradient( 135deg, rgb(15, 102, 184), rgb(18, 35, 65) );
        color: #ffffff;
        font-size: 13px;
        font-weight: 700;
        line-height: 1.4;
        border-radius: 12px 12px 0 0;
    }

    .ce-phone-row {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        box-sizing: border-box;
    }

    .ce-country-dropdown {
        width: 135px;
        min-width: 135px;
        max-width: 135px;
        flex: 0 0 135px;
    }

    .ce-phone-input {
        flex: 1;
        min-width: 0;
        width: 100%;
        height: 38px;
        padding: 7px 9px;
        border: 1px solid #cbd5e1;
        border-radius: 7px;
        box-sizing: border-box;
        color: #1e2d45;
        background: #ffffff;
        font-size: 12px;
        outline: none;
    }

        .ce-phone-input:focus {
            border-color: #0f66b8;
        }

    .ce-phone-error {
        margin-top: 6px;
        color: #dc2626;
        font-size: 11px;
    }

    .ce-phone-submit {
        width: 100%;
        margin-top: 12px;
    }

    .ce-text-message {
        max-width: 360px;
        padding: 10px 14px;
        background: #ffffff;
        border-radius: 6px 16px 16px 16px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        color: #333333;
        font-size: 13px;
        line-height: 1.5;
        box-sizing: border-box;
        white-space: normal;
        overflow-wrap: anywhere;
    }

    :deep(.ce-country-dropdown.p-dropdown) {
        width: 135px;
        min-width: 135px;
        max-width: 135px;
        height: 38px;
    }

    :deep(.ce-country-dropdown .p-dropdown-label) {
        padding: 8px 9px;
        font-size: 12px;
    }

    :deep(.p-dropdown-panel) {
        z-index: 99999 !important;
    }


    /* Duplicate Event action - visual only */
    .ce-duplicate-btn {
        min-width: 72px;
        height: 30px;
        padding: 5px 10px;
        border: 1px solid #0f66b8;
        border-radius: 6px;
        background: #0f66b8;
        color: #ffffff;
        font-family: inherit;
        font-size: 10px;
        font-weight: 700;
        line-height: 1;
        cursor: pointer;
        white-space: nowrap;
        box-sizing: border-box;
    }

        .ce-duplicate-btn:hover:not(:disabled) {
            background: #0c559b;
            border-color: #0c559b;
        }

        .ce-duplicate-btn:disabled {
            opacity: .5;
            cursor: not-allowed;
        }

    /* Invitation method - same white form/card feel as guest phone number */
    .ce-invite-card {
        min-width: 280px;
        max-width: 360px;
    }

    .ce-invite-choice-list {
        width: 100%;
        margin-top: 12px;
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px;
        box-sizing: border-box;
    }

    .ce-invite-choice-btn {
        width: 100%;
        height: 38px;
        padding: 7px 10px;
        border: 1px solid #cbd5e1;
        border-radius: 7px;
        background: linear-gradient(135deg, rgb(15, 102, 184), rgb(18, 35, 65));
        color: #ffffff;
        font-family: inherit;
        font-size: 12px;
        font-weight: 700;
        cursor: pointer;
        box-sizing: border-box;
    }

        .ce-invite-choice-btn:hover:not(:disabled) {
            opacity: .92;
        }

        .ce-invite-choice-btn:disabled {
            opacity: .5;
            cursor: not-allowed;
        }

    @media (max-width: 480px) {
        .ce-guest-row {
            grid-template-columns: minmax(0, 1fr) 72px;
            gap: 8px;
        }

            .ce-guest-row .ce-pill {
                min-width: 68px;
                font-size: 9px;
            }

        .ce-phone-card {
            max-width: 330px;
        }

        .ce-country-dropdown {
            width: 125px;
            min-width: 125px;
            max-width: 125px;
            flex-basis: 125px;
        }

        :deep(.ce-country-dropdown.p-dropdown) {
            width: 125px;
            min-width: 125px;
            max-width: 125px;
        }

        .ce-chore-details {
            padding: 10px 12px;
        }

        .ce-chore-detail-row > span {
            min-width: 104px;
        }
    }

    /* Small centered confirmation popup for Chore Remove only */
    .ce-confirm-overlay {
        position: fixed;
        inset: 0;
        z-index: 999999;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 16px;
        box-sizing: border-box;
        background: rgba(0, 0, 0, 0.48);
    }

    .ce-confirm-card {
        width: min(420px, calc(100vw - 32px));
        max-width: 420px;
        background: #ffffff;
        border-radius: 4px;
        overflow: hidden;
        box-shadow: 0 4px 18px rgba(0, 0, 0, 0.28);
        text-align: left;
    }

    .ce-confirm-header {
        min-height: 40px;
        display: flex;
        align-items: center;
        padding: 9px 13px;
        box-sizing: border-box;
        background: #132a4e;
        color: #ffffff;
        font-size: 14px;
        font-weight: 600;
        line-height: 1.3;
    }

    .ce-confirm-body {
        padding: 18px 13px 11px;
        background: #ffffff;
    }

    .ce-confirm-message {
        margin: 0;
        color: #222222;
        font-size: 13px;
        font-weight: 400;
        line-height: 1.5;
    }

    .ce-confirm-actions {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 8px;
        margin-top: 18px;
    }

    .ce-confirm-btn {
        min-width: 76px;
        height: 32px;
        padding: 5px 14px;
        border: 0;
        border-radius: 3px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
    }

    .ce-confirm-ok {
        background: #132a4e;
        color: #ffffff;
    }

        .ce-confirm-ok:hover {
            background: #0f223f;
        }

    .ce-confirm-cancel {
        background: #cc3b30;
        color: #ffffff;
    }

        .ce-confirm-cancel:hover {
            background: #b83228;
        }

    @media (max-width: 480px) {
        .ce-confirm-overlay {
            padding: 12px;
        }

        .ce-confirm-card {
            width: 100%;
            max-width: 360px;
        }

        .ce-confirm-header {
            font-size: 13px;
        }

        .ce-confirm-message {
            font-size: 12px;
        }
    }

    /* Small centered Event Guest remove popup */
    .ce-event-guest-confirm-overlay {
        position: fixed;
        inset: 0;
        z-index: 999999;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 16px;
        box-sizing: border-box;
        background: rgba(0, 0, 0, 0.48);
    }

    .ce-event-guest-confirm-card {
        width: min(360px, calc(100vw - 32px));
        max-width: 360px;
        background: #ffffff;
        border-radius: 6px;
        overflow: hidden;
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.30);
        text-align: left;
    }

    .ce-event-guest-confirm-header {
        min-height: 42px;
        display: flex;
        align-items: center;
        padding: 10px 16px;
        box-sizing: border-box;
        background: #1e2d45;
        color: #ffffff;
        font-size: 14px;
        font-weight: 700;
    }

    .ce-event-guest-confirm-body {
        padding: 18px 16px 14px;
        background: #ffffff;
    }

    .ce-event-guest-confirm-message {
        margin: 0;
        color: #1e2d45;
        font-size: 13px;
        line-height: 1.5;
    }

    .ce-event-guest-confirm-actions {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 9px;
        margin-top: 18px;
    }

    .ce-event-guest-confirm-btn {
        min-width: 76px;
        height: 32px;
        padding: 6px 14px;
        border: 0;
        border-radius: 4px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
    }

    .ce-event-guest-confirm-ok {
        background: #1e2d45;
        color: #ffffff;
    }

    .ce-event-guest-confirm-cancel {
        background: #dc2626;
        color: #ffffff;
    }

    .ce-event-guest-confirm-ok:hover {
        background: #142137;
    }

    .ce-event-guest-confirm-cancel:hover {
        background: #b91c1c;
    }

    .ce-guest-email,
    .ce-guest-phone {
        display: block;
        width: 100%;
        overflow-wrap: anywhere;
    }

    .ce-guest-phone {
        margin-top: 2px;
    }

    /* Event Details - guest delete icon like portal */
    .ce-guest-info {
        flex: 1;
        min-width: 0;
    }

    .ce-guest-row-actions {
        display: flex;
        align-items: center;
        gap: 7px;
        flex-shrink: 0;
    }

    .ce-guest-delete-icon {
        width: 28px;
        min-width: 28px;
        height: 32px;
        padding: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: 1px solid #ef4444;
        border-radius: 6px;
        background: #ffffff;
        color: #ef4444;
        cursor: pointer;
        box-sizing: border-box;
        line-height: 0;
        flex-shrink: 0;
        transition: background .15s ease, border-color .15s ease, color .15s ease;
    }

        .ce-guest-delete-icon svg {
            display: block;
            width: 13px;
            height: 13px;
            flex-shrink: 0;
            pointer-events: none;
        }

        .ce-guest-delete-icon:hover:not(:disabled) {
            background: #fff1f2;
            color: #dc2626;
            border-color: #dc2626;
        }

        .ce-guest-delete-icon:active:not(:disabled) {
            background: #fee2e2;
        }

        .ce-guest-delete-icon:disabled {
            opacity: 0.45;
            cursor: not-allowed;
        }

    /* =========================================================
       Event Details - Past state + compact guest action design
       ========================================================= */

    .ce-event-detail-card .ticket-preview-header {
        align-items: center;
    }

    .ce-event-detail-status {
        flex: 0 0 auto;
        min-width: 44px;
        padding: 3px 9px;
        border-radius: 12px;
        font-size: 10px;
        font-weight: 700;
        line-height: 1.2;
        text-align: center;
        box-sizing: border-box;
    }

    .ce-event-detail-status--past {
        background: rgba(255, 255, 255, 0.16);
        border: 1px solid rgba(255, 255, 255, 0.35);
        color: #ffffff;
    }

    .ce-event-detail-card .ce-guest-row-actions {
        padding: 4px 5px;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        background: #f8fafc;
    }

    .ce-event-detail-card .ce-guest-row .ce-list-item__tags {
        /*min-width: 68px;*/
        flex-direction: row;
        padding-top: 4px;
    }

    .ce-event-detail-card .ce-guest-delete-icon {
        width: 28px;
        min-width: 28px;
        height: 32px;
        border-radius: 6px;
        background: #ffffff;
    }

    .ce-event-detail-actions {
        padding-top: 10px;
        border-top: 1px solid #eef2f7;
    }

    .ce-event-detail-add-guest {
        width: 100%;
        min-height: 38px;
        box-sizing: border-box;
    }

    .ce-event-detail-add-guest--past:disabled {
        background: #f1f5f9 !important;
        border: 1px solid #cbd5e1 !important;
        color: #94a3b8 !important;
        opacity: 1 !important;
        cursor: not-allowed !important;
    }

    @media (max-width: 480px) {
        .ce-event-detail-card .ce-guest-row-actions {
            gap: 5px;
            padding: 4px;
        }

        .ce-event-detail-status {
            min-width: 40px;
            padding: 3px 7px;
            font-size: 9px;
        }
    }

    /* Chore Start Date calendar — no icon, fit chatbot width, past disabled via minDate */
    .ce-calendar {
        width: 100%;
        max-width: 100%;
    }

    :deep(.ce-calendar.p-calendar) {
        width: 100%;
        max-width: 100%;
    }

    :deep(.ce-calendar .p-inputtext) {
        width: 100%;
        min-height: 38px;
        padding: 7px 9px;
        border: 1px solid #cbd5e1;
        border-radius: 7px;
        font-size: 12px;
        color: #1e2d45;
        box-sizing: border-box;
    }

    :deep(.ce-calendar .p-datepicker) {
        width: min(280px, 100%) !important;
        max-width: 100% !important;
        min-width: 0 !important;
        box-sizing: border-box;
        z-index: 999999 !important;
    }

    :deep(.ce-calendar .p-datepicker-header) {
        padding: 0.5rem;
    }

    :deep(.ce-calendar .p-datepicker table) {
        width: 100%;
        font-size: 12px;
    }

    :deep(.ce-calendar .p-datepicker table td > span) {
        width: 1.8rem;
        height: 1.8rem;
        line-height: 1.8rem;
    }

    :deep(.ce-calendar .p-datepicker-buttonbar) {
        padding: 0.4rem;
    }
    /* Hide any residual calendar trigger icon */
    :deep(.ce-calendar .p-datepicker-trigger),
    :deep(.ce-calendar .p-calendar-w-btn .p-datepicker-trigger) {
        display: none !important;
    }

    /* Native select styled like the other inputs */
    .ce-select {
        height: 40px;
        padding-right: 32px;
        cursor: pointer;
        appearance: none;
        -webkit-appearance: none;
        background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path d='M1 1l5 5 5-5' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/></svg>");
        background-repeat: no-repeat;
        background-position: right 12px center;
    }

    /* =========================================================
       MOBILE FIT - chatbot cards (keep this block LAST)
       Card width is taken from the screen width so it does not
       depend on the parent bubble layout. Tune the 105px value:
       larger = narrower card, smaller = wider card.
       ========================================================= */
    @media (max-width: 480px) {
        .ce-card,
        .ce-card--form,
        .ce-chore-status-card,
        .ce-status-flow-card,
        .ce-weekdays-card,
        .ce-event-guest-card,
        .ce-event-guest-source-card,
        .ce-phone-card,
        .ce-invite-card,
        .ce-text-message {
            width: calc(100vw - 105px) !important;
            min-width: 0 !important;
            max-width: calc(100vw - 105px) !important;
            margin-right: 0 !important;
            box-sizing: border-box !important;
        }

        .ce-event-guest-source-header,
        .ce-event-guest-header {
            padding: 11px 12px;
            font-size: 12px;
            line-height: 1.45;
            overflow-wrap: anywhere;
        }

        .ce-event-guest-source-options {
            padding: 8px;
            gap: 6px;
        }

        .ce-event-guest-source-option {
            min-height: 38px;
            padding: 8px 10px;
        }

        .ce-event-guest-card .ce-card__body {
            padding: 10px 12px !important;
        }

        .ce-event-guest-card .ce-card__actions {
            padding: 0 12px 12px !important;
        }

        .ce-phone-card {
            padding: 14px;
        }

        .ce-phone-row,
        .ce-invite-choice-list {
            gap: 6px;
        }
    }
</style>

<style>
    /* Dropdown / calendar panels are appended to <body>, so they need
    global (non-scoped) rules to appear above the chatbot window. */
    .p-dropdown-panel,
    .p-datepicker.p-component {
        z-index: 2147483647 !important;
    }

    @media (max-width: 480px) {
        .p-dropdown-panel {
            max-width: calc(100vw - 24px) !important;
        }

            .p-dropdown-panel .p-dropdown-items-wrapper {
                max-height: 220px !important;
                overflow-y: auto !important;
            }
    }
</style>
